/* global gtag */
import React, { useState, useEffect, useRef } from "react";
import { BarChart2 } from "lucide-react";
import { Grid, Button, IconButton,TextField, Typography, Card, CardMedia, CardContent, Autocomplete, FormControlLabel, Checkbox, CircularProgress, Popper, Dialog, DialogTitle, DialogContent, Chip, Box, Stack } from "@mui/material";
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import LogRocket from 'logrocket';
import './App.css';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import debounce from "lodash.debounce";
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import './ReelyAutocomplete.css';

LogRocket.init('pq0kyv/reely', {
  sampleRate: 10, // 10% of sessions recorded
});

const supabaseUrl = "https://fsfjrplooiozffzzrymk.supabase.co"; // ✅ Supabase URL
const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZzZmpycGxvb2lvemZmenpyeW1rIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE2Mjk3MTQsImV4cCI6MjA1NzIwNTcxNH0.pffZenfEVxvKPZWmV8TRWZrSXY7Qd7s8B_2cTDbtf60"; // ✅ Supabase Key
export const supabase = createClient(supabaseUrl, supabaseAnonKey); // ✅ Initialize Supabase client

// 🔢 STATS MODAL COMPONENT
export function StatsModal({ open, onClose, userId, userStats, reelStats }) {
    const StatBox = ({ value, label }) => (
      <div style={{ textAlign: "center", margin: "10px", flex: 1 }}>
        <div style={{ fontSize: "24px", fontWeight: "600" }}>{value ?? "-"}</div>
        <div style={{ fontSize: "10px", color: "#555", marginTop: "4px" }}>{label}</div>
      </div>
    );
  
    return (
        <Dialog open={open} onClose={onClose}>
        <DialogTitle
          style={{
            fontFamily: "'Montserrat'",
            fontWeight: "700",
            fontSize: "22px",
            textAlign: "center",
          }}
        >
          📊 Your Stats
        </DialogTitle>
      
        <DialogContent
          style={{
            overflowX: "hidden",         // prevent horizontal scroll
            padding: "16px",             // default padding
            maxWidth: "100vw",           // constrain to viewport
            boxSizing: "border-box",     // includes padding in width
          }}
        >
          <div
            style={{
              fontFamily: "'Montserrat'",
              fontSize: "13px",
              lineHeight: "1.6",
              maxWidth: "320px",         // control inner layout width
              margin: "0 auto",          // center content
            }}
          >
            {/* Row 1 */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <StatBox value={userStats?.total_reels_completed} label="Total Reels" />
              <StatBox value={userStats?.current_streak} label="Current Streak" />
              <StatBox value={userStats?.longest_streak} label="Longest Streak" />
            </div>
      
            {/* Row 2 */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <StatBox value={reelStats?.avg?.toFixed(1)} label="Average Reel Length" />
              <StatBox value={reelStats?.min} label="Shortest Reel" />
              <StatBox value={reelStats?.max} label="Longest Reel" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
    );
} 

let copyrightLogged = false;

// ✅ Function to log copyright notice
const logCopyrightNotice = () => {
  if (copyrightLogged) return; // ✅ Already logged, skip
  console.log("🛡️ © 2025 Reely. All rights reserved.");
  console.log("📜 This game and its underlying concept are protected by copyright, trademark, and applicable intellectual property law.");
  copyrightLogged = true; // ✅ Set flag
};    

function App() {
    const isBreely = typeof window !== "undefined" && window.location.hostname.includes("breely");
    const [startMovie, setStartMovie] = useState("");
    const [endMovie, setEndMovie] = useState("");
    const [currentMovie, setCurrentMovie] = useState("");
    const [currentActor, setCurrentActor] = useState("");
    const [userInput, setUserInput] = useState("");
    const [route, setRoute] = useState([]); // Store array of { name, image }
    const [error, setError] = useState("");
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [steps_taken, setsteps_taken] = useState(0); // 🔢 Track number of moves
    const [actorImage, setActorImage] = useState(null);
    const [startMoviePoster, setStartMoviePoster] = useState(null);  // ✅ New state for start movie poster
    const [moviePoster, setMoviePoster] = useState(null);  // ✅ Used for user-entered movies
    const [endMoviePoster, setEndMoviePoster] = useState(null);
    const backendUrl = "https://reely-backend.onrender.com";
    const [searchTerm, setSearchTerm] = useState({label: ""});
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false)
    const [gameFinished, setGameFinished] = useState(false); // ✅ Prevents multiple triggers
    const [userId, setUserId] = useState(null);
    const [isGameComplete, setIsGameComplete] = useState(false); // ✅ Track game completion
    const [theme, setTheme] = useState(""); // ✅ Declare theme state
    const [isSearchFocused, setIsSearchFocused] = useState(false); // ✅ Track search focus state
    const [isMobile, setIsMobile] = useState(false); // ✅ Track mobile device state
    const [hasInputError, setHasInputError] = useState(false);
    const lastEntryRef = useRef(null);
    const [openCongratsDialog, setOpenCongratsDialog] = useState(false);
    const [openShareOptions, setOpenShareOptions] = useState(false);
    const boostedMatches = [];
    const fallbackMatches = [];
    const [currentStreak, setCurrentStreak] = useState(null);
    const [rewindCount, setRewindCount] = useState(0);
    const isActor = currentMovie !== "";  // because actors come after movies
    const [endMovieId, setEndMovieId] = useState(null);
    const [showEndMovieTooltip, setShowEndMovieTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const [dailyGifUrl, setDailyGifUrl] = useState("");
    const [isLoadingCast, setIsLoadingCast] = useState(false);
    const [showLongLoadMessage, setShowLongLoadMessage] = useState(false);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [userStats, setUserStats] = useState(null);
    const [reelStats, setReelStats] = useState(null);
    const [startMovieCast, setStartMovieCast] = useState([]);
    const [gameId, setGameId] = useState(null)
    const topContainerRef = useRef(null);
    const [isTodayGameComplete, setIsTodayGameComplete] = useState(false);
    const indianLanguages = new Set(['hi', 'ta', 'te', 'ml', 'kn', 'mr', 'bn']);
    const [showBreelyErrorTip, setShowBreelyErrorTip] = useState(false);
    const [isReelHidden, setIsReelHidden] = useState(true);
    const displayUrl = isBreely ? "www.playbreely.com" : "www.playreely.com";
    const [includeSpoilers, setIncludeSpoilers] = useState(false);
    const [hintOptions, setHintOptions] = useState([]);
    const [showHintOverlay, setShowHintOverlay] = useState(false);
    const [hintAnchorIndex, setHintAnchorIndex] = useState(null); // index of clicked movie in the route
    const [isStartEndHint, setIsStartEndHint] = useState(false);
    const [hintSourceName, setHintSourceName] = useState("");
    const [hintCount, setHintCount] = useState(0);
    const [hintedItems, setHintedItems] = useState(new Set());
    const [showHintTooltip, setShowHintTooltip] = useState(false);
    const hintTooltipRef = useRef(null);
    const [tooltipInteractive, setTooltipInteractive] = useState(false);
    const [showBreelyMaintenance, setShowBreelyMaintenance] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [showInstaPrompt, setShowInstaPrompt] = useState(false);
    const instaPromptRef = useRef(null);
    const [finalFrameCount, setFinalFrameCount] = useState(null);
    const [averageFrameCount, setAverageFrameCount] = useState(null);
    const parsedAverage = averageFrameCount ? Number(averageFrameCount).toFixed(1) : null;
    const [minimumFrameCount, setMinimumFrameCount] = useState(null);
    const [showOutageBanner, setShowOutageBanner] = useState(true);


    const normalizeString = (str) => {
      if (typeof str !== "string") return "";
      return str.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
    };     

    const TMDB_API_KEY = "9fd3ab94d965f38b3b67d6dfde8651fe"; // TMDB API Key

    useEffect(() => {
      if (isBreely !== undefined) {
        document.title = isBreely
          ? "Breely — The Bollywood Connection Game"
          : "Reely — The Movie Connection Game";
      }
    }, []);

    useEffect(() => {
      if (isBreely) {
        setShowBreelyMaintenance(true);
      }
    }, [isBreely]);    

    const baseUrl = isBreely
      ? "https://playbreely.com"
      : "https://playreely.com"; // or your Reely URL

   
    const getUserCookie = () => {
        let cookie = document.cookie
            .split("; ")
            .find(row => row.startsWith("reely_user="));
        return cookie ? cookie.split("=")[1] : null;
    };
   
    const setUserCookie = (userId) => {
        document.cookie = `reely_user=${userId}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
        console.log("🍪 User Cookie Set:", userId);
    };
   
    const isValidUUID = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(id);

      // 🧠 Log copyright and check existing user cookie on mount
      useEffect(() => {
        logCopyrightNotice();
        const existingUserId = getUserCookie();
        if (existingUserId && isValidUUID(existingUserId) && !existingUserId.startsWith("guest-")) {
          console.log("✅ Valid User Cookie Found:", existingUserId);
        }
      }, []);
    
    const getOrCreateUser = async () => {
      let existingUserId = getUserCookie();
    
      if (existingUserId) {
        const isUUID = isValidUUID(existingUserId);
        const isLegacyGuest = existingUserId.startsWith("guest-");
    
        if (isUUID && !isLegacyGuest) {
          setUserId(existingUserId);  // No need to log here anymore
          return existingUserId;
        } else {
          console.warn("⚠️ Legacy or Invalid User ID found. Removing cookie:", existingUserId);
          document.cookie = `reely_user=; path=/; max-age=0; SameSite=Lax`;
        }
      }

    const newUserId = uuidv4(); // ✅ Clean UUID
    setUserCookie(newUserId);
    setUserId(newUserId);

    const { error } = await supabase
        .from("usersv2")
        .insert([{ user_id: newUserId }]);

    if (error) {
        console.error("❌ Error creating user in Supabase:", error);
        return null;
    }

    console.log("🎉 New User Created & Saved:", newUserId);
    return newUserId;
};
  
/*
    useEffect(() => {
        const waitForUser = async () => {
            const userId = await getOrCreateUser();
            if (userId) {
                console.log("✅ Ready to identify user in LogRocket:", userId);
                LogRocket.identify(userId, { anonymous: true });
                await fetchUserStreak();
                console.log("🎥 LogRocket identify fired:", userId);
            }
        };
    
        waitForUser();
    }, []);
    */

    useEffect(() => {
      const initializeApp = async () => {
        const userId = await getOrCreateUser();
        if (!userId) return;
    
        try {
          await Promise.all([
            supabase.from("PageVisits").insert([{ user_id: userId, visited_at: new Date().toISOString() }])
              .then(() => console.log("👀 Page visit logged"))
              .catch((error) => console.error("❌ Failed to log page visit:", error)),
            fetchUserStreak(),
            fetchRandomMovies(userId)
          ]);
        } catch (error) {
          console.error("❌ Error during app initialization:", error);
        }
      };
      initializeApp();
    }, []);    

    useEffect(() => {
      const testSupabase = async () => {
        try {
          const { error } = await supabase.from('daily_movie_pair').select('*').limit(1);
          if (!error) setShowOutageBanner(false);
        } catch (err) {
          // still down, keep showing banner
        }
      };
      testSupabase();
    }, []);


    // ✅ Save game state to localStorage
      useEffect(() => {
        if (isGameStarted) {
          const savedState = {
            route,
            currentMovie,
            currentActor,
            steps_taken,
            startMovie,
            endMovie,
            startMoviePoster,
            endMoviePoster,
            endMovieId,
            isGameComplete,
            gameDate: getTodayInPST(),
          };
          localStorage.setItem("reelState", JSON.stringify(savedState));
          console.log("💾 Saved reel state to localStorage:", savedState);
        }
      }, [route, currentMovie, currentActor, steps_taken, isGameStarted]);
    

    // ✅ Load game state from localStorage
    useEffect(() => {
      const saved = localStorage.getItem("reelState");
      if (saved) {
        const savedState = JSON.parse(saved);
        const today = getTodayInPST();
    
        if (savedState?.gameDate === today) {
          console.log("🧠 Restoring saved reel state:", savedState);
    
          setRoute(savedState.route || []);
          setCurrentMovie(savedState.currentMovie || "");
          setCurrentActor(savedState.currentActor || "");
          setsteps_taken(savedState.steps_taken || 0);
          setStartMovie(savedState.startMovie || "");
          setEndMovie(savedState.endMovie || "");
          setStartMoviePoster(savedState.startMoviePoster || null);
          setEndMoviePoster(savedState.endMoviePoster || null);
          setEndMovieId(savedState.endMovieId || null);
          setIsGameComplete(savedState.isGameComplete || false);
          setIsGameStarted(true); // ✅ This triggers the correct view
        } else {
          // 🧼 Clear stale saved state from a different day
          localStorage.removeItem("reelState");
        }
      }
    }, []);

    // ✅ Check if today's game is complete
    useEffect(() => {
      const savedCompletionStatus = localStorage.getItem("isTodayGameComplete");
      if (savedCompletionStatus === "true") {
        setIsTodayGameComplete(true);
      }
    }, []);

    
    // ✅ Fetch current streak from Supabase
    const fetchUserStreak = async () => {
        const userId = await getOrCreateUser();
        if (!userId) return;
      
        const { data, error } = await supabase
          .from("usersv2")
          .select("current_streak")
          .eq("user_id", userId)
          .maybeSingle();
      
        if (error) {
          console.error("❌ Failed to fetch streak:", error.message);
        } else if (data) {
          console.log("🔥 Current Streak Fetched:", data.current_streak);
          setCurrentStreak(data.current_streak);
        }
      };     

      // Fetch global average from supabase for game refresh use case
      useEffect(() => {
        if (isGameComplete && startMovie && endMovie) {
          console.log("📊 Fetching average frame count...");
      
          supabase
            .rpc("get_average_steps", {
              start_movie_input: startMovie,
              end_movie_input: endMovie,
            })
            .then(({ data, error }) => {
              if (error) {
                console.error("❌ Failed to fetch average frame count:", error);
              } else if (data !== null) {
                setAverageFrameCount(parseFloat(data).toFixed(2));
              }
            });
        }
      }, [isGameComplete, startMovie, endMovie]);

      // Fetch global minimum from Supabase for game refresh use case
      useEffect(() => {
        if (isGameComplete && startMovie && endMovie) {
          console.log("🥇 Fetching minimum frame count...");

          supabase
            .rpc("get_min_steps", {
              start_movie_input: startMovie,
              end_movie_input: endMovie,
            })
            .then(({ data, error }) => {
              if (error) {
                console.error("❌ Failed to fetch minimum frame count:", error);
              } else if (data !== null) {
                setMinimumFrameCount(data);
              }
            });
        }
      }, [isGameComplete, startMovie, endMovie]);
      
      
      const [openHowToPlay, setOpenHowToPlay] = useState(false);

      useEffect(() => {
        const hasSeenHowToPlay = localStorage.getItem("hasSeenHowToPlay");
      
        if (!hasSeenHowToPlay) {
          setOpenHowToPlay(true);
          localStorage.setItem("hasSeenHowToPlay", "true");
        }
      }, []);      

      // 1. Check localStorage on mount
      useEffect(() => {
        const hasSeenHintTooltip = localStorage.getItem("hasSeenHintTooltip");
      
        if (!hasSeenHintTooltip && isGameStarted && route.length === 1) {
          const delay = setTimeout(() => {
            setShowHintTooltip(true); // ✅ show tooltip after delay
          }, 5000); // 1 second delay
      
          return () => clearTimeout(delay); // Cleanup if unmounted
        }
      }, [isGameStarted, route.length]);
      

        const handleDismissHintTooltip = () => {
          setShowHintTooltip(false);
          localStorage.setItem("hasSeenHintTooltip", "true");
        };       

        // 2. Handle outside clicks with delay
        useEffect(() => {
          const handleClickOutside = (e) => {
            if (hintTooltipRef.current && !hintTooltipRef.current.contains(e.target)) {
              setShowHintTooltip(false);
              localStorage.setItem("hasSeenHintTooltip", "true");
            }
          };
        
          if (showHintTooltip) {
            document.addEventListener("click", handleClickOutside);
          }
        
          return () => {
            document.removeEventListener("click", handleClickOutside);
          };
        }, [showHintTooltip]);   

    const handleDismissInstaPrompt = () => {
      setShowInstaPrompt(false);
      localStorage.setItem("reelySeenInstaPrompt", "true");
    };

    // ✅ Save game data when the user refreshes or closes the tab
    useEffect(() => {
        const handleBeforeUnload = () => {
            saveGameData(startMovie, endMovie, steps_taken, false, "refreshed");
        };
    
        window.addEventListener("beforeunload", handleBeforeUnload);
    
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [startMovie, endMovie, steps_taken]); // ✅ Dependencies ensure latest values are used
    
    useEffect(() => {
        console.log("🚀 Warming up API...");
      
        fetch(`${backendUrl}/validate-connection?movieId=550&actorName=Brad%20Pitt`)
          .then(() => console.log("✅ API Warmed Up via /validate-connection"))
          .catch(() => console.warn("⚠ API Warmup Failed"));
      }, []);

      useEffect(() => {
        console.log("🔍 Route on update:", route);
      
        if (route.length === 4 && !localStorage.getItem("seenEndMovieTooltip")) {
        console.log("✅ Showing tooltip");
            // Delay to ensure UI has updated
          setTimeout(() => {
            setShowEndMovieTooltip(true);
          }, 2000); // 100ms delay
        }
      }, [route]); // ✅ This goes outside the useEffect body

      // ✅ Hide hint drop down when clicked
      useEffect(() => {
        const handleClickOutside = (e) => {
          if (!e.target.closest(".hint-overlay")) {
            setShowHintOverlay(false);
          }
        };
      
        if (showHintOverlay) {
          document.addEventListener("mousedown", handleClickOutside);
        }
      
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }, [showHintOverlay]);      
      
    // Log game start in Supabase
    const logGameStart = async (gameId) => {
        try {
            await supabase.from("Games").update({ game_started_at: new Date().toISOString() }).eq("id", gameId);
            console.log("✅ Game start logged in Supabase");
        } catch (error) {
            console.error("❌ Error logging game start:", error);
        }
    };

    useEffect(() => {
      const timeout = setTimeout(() => {
        if (lastEntryRef.current) {
          lastEntryRef.current.scrollIntoView({
            behavior: "auto",
            inline: "end",
            block: "nearest",
          });
        }
    
        // ✅ Scroll the top container into view (frame count stays visible)
        if (topContainerRef.current) {
          topContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    
      return () => clearTimeout(timeout);
    }, [route, isGameStarted, endMovie]); // 👈 track endMovie to re-trigger when it changes
          
    const startNewGame = async (userId) => {
      try {
        if (!userId) {
          console.error("❌ No userId provided to startNewGame.");
          return;
        }
    
        const { data, error } = await supabase
          .from("Games")
          .insert([
            {
              user_id: userId,
              start_movie: null,
              end_movie: null,
              steps_taken: 0,
              completed_at: null
            }
          ])
          .select("id")
          .single();
    
        if (error) throw error;
        console.log("📝 Game row created (but not started):", data);
        return data.id;
      } catch (error) {
        console.error("❌ Error creating game:", error);
      }
    };

    const getTodayInPST = () => {
      return new Date().toLocaleDateString("en-CA", {
        timeZone: "America/Los_Angeles",
      }); // e.g. "2025-03-28"
    };
/*
    const getTodayInPST = () => {
      return "2025-07-05"; // Pretend it's tomorrow
    };    
*/
          
        const getYesterdayInPST = () => {
            const pstNow = new Date(
              new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
            );
            pstNow.setDate(pstNow.getDate() - 1);
            return pstNow.toLocaleDateString("en-CA");
          };          

        const formatTodayDate = () => {
            const today = new Date();
            return today.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric"
            });
          }; 

        function getTimeUntilNextPSTMidnight() {
        const now = new Date();
        
        // Convert current time to UTC
        const nowUTC = new Date(
            now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
        );
        
        const nextMidnight = new Date(nowUTC);
        nextMidnight.setHours(24, 0, 0, 0); // Set to next midnight PST
        
        const diff = nextMidnight - nowUTC;
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);
        
        return { hours, minutes, seconds };
        }

        const [countdown, setCountdown] = useState(getTimeUntilNextPSTMidnight());

        useEffect(() => {
        const interval = setInterval(() => {
            setCountdown(getTimeUntilNextPSTMidnight());
        }, 1000); // update every second

        return () => clearInterval(interval);
        }, []);

          // ✅ Reset 'game played today' if date changes
          const today = getTodayInPST();
          const lastPlayedDate = localStorage.getItem("lastPlayedDate");

          if (lastPlayedDate !== today) {
            localStorage.removeItem("isTodayGameComplete");
            localStorage.removeItem("reelState");
            localStorage.setItem("lastPlayedDate", today);
          }

          const debouncedFetchSuggestions = useRef(
            debounce(async (input, expectMovie) => {
              const start = performance.now();
              await fetchSuggestions(input, expectMovie);
              const end = performance.now();
          
              // ✅ LogRocket tracking
              try {
                LogRocket.log(`[Latency] fetchSuggestions: ${Math.round(end - start)}ms`);
              } catch (err) {
                console.warn("LogRocket.log failed:", err);
              }
            }, 150)
          ).current;          

        const fetchAndStoreStartMovieCast = async (startMovieId, gameDate) => {
          try {
            const tableName = isBreely ? "breely_daily_movie" : "daily_movie_pair";
        
            // Check if cast already exists in Supabase
            const { data: existingData, error: existingError } = await supabase
              .from(tableName)
              .select("start_movie_cast")
              .eq("game_date", gameDate)
              .single();
        
            if (existingError) {
              console.error("❌ Error checking for existing cast:", existingError);
              return;
            }
        
            if (Array.isArray(existingData?.start_movie_cast) && existingData.start_movie_cast.length > 0) {
              console.log("✅ Cast already stored in Supabase, skipping fetch.");
              return;
            }            
        
            // Fetch cast from TMDb
            const url = `https://api.themoviedb.org/3/movie/${startMovieId}/credits?api_key=${TMDB_API_KEY}`;
            const response = await fetch(url);
            const json = await response.json();
        
            const topCast = json.cast
            .sort((a, b) => b.popularity - a.popularity)
            .slice(0, 15)
            .map((actor) => ({
              name: actor.name,
              id: actor.id,
            }));          
        
            console.log("🎭 Fetched top cast for start movie:", topCast);
        
            // Update Supabase
            const { error: updateError } = await supabase
              .from(tableName)
              .update({ start_movie_cast: topCast })
              .eq("game_date", gameDate);
        
            if (updateError) {
              console.error("❌ Error updating Supabase with cast:", updateError);
            } else {
              console.log(`✅ Supabase updated with start movie cast in "${tableName}"`);
            }
        
          } catch (error) {
            console.error("❌ Error fetching or storing start movie cast:", error);
          }
        };

const fetchRandomMovies = async (userId) => {

  if (!userId) {
    console.warn("⚠️ No userId passed to fetchRandomMovies, fetching from cookie...");
    userId = getUserCookie();
    if (!userId) {
      userId = await getOrCreateUser();
    }
    if (!userId) {
      console.error("❌ Still no userId after fallback attempt.");
      return;
    }
  }
  
  const tableName = isBreely ? "breely_daily_movie" : "daily_movie_pair";
  /*
  console.log("🧪 Hostname-based fallback:");
  console.log("🧪 isBreely:", isBreely);
  console.log("🧪 Supabase table being used:", tableName);
  */

    try {
      setIsGameStarted(false);
      setRoute([]);
      setUserInput("");
      setCurrentActor("");
      setMoviePoster(null);
      setActorImage(null);
      setError("");
      setsteps_taken(0);
      setCurrentMovie("");
      setIsGameComplete(false);

      if (!userId) return;
  
      const gameId = await startNewGame(userId);
      setGameId(gameId); 
      const today = getTodayInPST();
  
      const { data, error } = await supabase
        .from(tableName)
        .select("*")
        .eq("game_date", today)
        .limit(1)
        .single();
  
      if (error) {
        console.error("❌ Error fetching today’s movie pair:", error.message);
        setError("No movie pair found for today.");
        return;
      }
  
      const curatedPair = data;
      console.log("🎬 Today’s Daily Pair:", curatedPair);

      if (curatedPair.start_movie_cast) {
        try {
          const cast = Array.isArray(curatedPair.start_movie_cast)
            ? curatedPair.start_movie_cast
            : JSON.parse(curatedPair.start_movie_cast);
      
          setStartMovieCast(cast);
          console.log("🎭 Loaded start movie cast from Supabase:", cast);
        } catch (err) {
          console.error("❌ Failed to parse start_movie_cast:", err);
          setStartMovieCast([]);
        }      
      } else {
        await fetchAndStoreStartMovieCast(curatedPair.start_movie_id, today);
      }      

      const defaultGif = isBreely
        ? "https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExN2YyeXpydjl5MWkwMGd0ZjFqcGE3N3V4ZXcyZTFwZHM2ZTNtazQ5NyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/fTajeyAusMPoJqpFOZ/giphy.gif" // 🇮🇳 Bollywood/celebration vibe
        : "https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExa3Nyd2JpZWZ4c2V0dWF0bjh6Z2w0cGVyMWRqMjI5eXAwYmRibTFrNiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xT9DPC0o6lUwV4QCAM/giphy.gif"; // 🎬 Reely default

      setDailyGifUrl(curatedPair.gif_url ? curatedPair.gif_url : defaultGif);


      const fetchMovieDetailsById = async (movieId) => {
        try {
          // 1️⃣ Pull all matching rows from Supabase
          const { data: cachedRows, error: cacheError } = await supabase
            .from('actor_movie_connections')
            .select('movie_name, movie_id, movie_poster')
            .eq('movie_id', movieId);
      
          if (cacheError) {
            console.error('❌ Supabase cache fetch error:', cacheError);
          }
      
          // 2️⃣ Find best cached row (prefer row with poster)
          let cached = null;
          if (cachedRows && cachedRows.length > 0) {
            cached = cachedRows.find(r => r.movie_poster) || cachedRows[0];
          }
      
          if (cached) {
            console.log('📦 Movie details from cache:', cached);
            return {
              title: cached.movie_name,
              poster: cached.movie_poster,
              id: cached.movie_id,
            };
          }
      
          // 3️⃣ Fallback to TMDb API if no cache hit
          console.log('🌍 No cache link. Fetching from TMDb...');
          const url = `https://api.themoviedb.org/3/movie/${movieId}?api_key=${TMDB_API_KEY}`;
          const response = await fetch(url);
          const movie = await response.json();
      
          return {
            title: movie.title
              ? `${movie.title} (${movie.release_date?.slice(0, 4)})`
              : null,
            poster: movie.poster_path
              ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
              : null,
            id: movie.id,
          };
        } catch (err) {
          console.error('❌ Error fetching movie details:', err);
          return {
            title: null,
            poster: null,
            id: movieId,
          };
        }
      };
        
  
      const startMovieData = await fetchMovieDetailsById(curatedPair.start_movie_id);
      const endMovieData = await fetchMovieDetailsById(curatedPair.end_movie_id);

      console.log("🆔 Fetched by TMDb IDs:", {
        start_movie_id: curatedPair.start_movie_id,
        startMovieData,
        end_movie_id: curatedPair.end_movie_id,
        endMovieData,
      });
      
  
      if (gameId) {
        console.log(`📝 Attempting to update game ID: ${gameId} with movies: ${startMovieData.title} → ${endMovieData.title}`);
  
        const { data: updated, error: updateError } = await supabase
          .from("Games")
          .update({
            start_movie: startMovieData.title,
            end_movie: endMovieData.title,
          })
          .eq("id", gameId)
          .select();
  
        if (updateError) {
          console.error("❌ Error updating game with start/end movies:", updateError);
        } else {
          console.log(`✅ Game updated successfully for ID: ${gameId}`);
        }
      }
  
      setStartMovie(startMovieData.title);
      setEndMovie(endMovieData.title);
      setStartMoviePoster(startMovieData.poster);
      setEndMoviePoster(endMovieData.poster);
      setEndMovieId(endMovieData.id);
      setTheme(curatedPair.theme || "");

      const savedState = JSON.parse(localStorage.getItem("reelState"));

      const isValidSavedState =
        savedState &&
        savedState.gameDate === getTodayInPST() &&
        savedState.startMovie === startMovieData.title &&
        savedState.endMovie === endMovieData.title;

      if (isValidSavedState) {
        console.log("🧠 Restoring saved reel state:", savedState);
        setRoute(savedState.route || []);
        setCurrentMovie(savedState.currentMovie || "");
        setCurrentActor(savedState.currentActor || "");
        setsteps_taken(savedState.steps_taken || 0);
        setIsGameComplete(savedState.isGameComplete || false);
        setIsGameStarted(true);
      } else {
        // Reset defaults only if not restoring
        setRoute([]);
        setUserInput("");
        setCurrentActor("");
        setActorImage(null);
        setsteps_taken(0);
        setSearchTerm({ label: "" });
        setCurrentMovie(startMovieData.title);
        setIsGameComplete(false);
        setError("");
      }
  
      await fetch(
        `https://api.themoviedb.org/3/movie/${startMovieData.id}/credits?api_key=${TMDB_API_KEY}`
      );
      console.log("⚡ Preloading cast for:", startMovieData.title);
    } catch (error) {
      console.error("❌ Error fetching curated movies:", error);
      setError("Error fetching curated movies.");
    }
  };

    const handleStartGame = () => {
        
        if (!startMovie || !startMoviePoster) {
            setError("Game is still loading movies...");
            return;
          }
        
          setRoute([
            {
              name: startMovie,
              image: startMoviePoster,
              type: "movie", 
            },
          ]);
        
          setsteps_taken(0);
          setCurrentMovie(startMovie);
          setHintCount(0);
          setHintedItems(new Set());
          setCurrentActor("");
          setUserInput("");
          setError("");
          setIsGameStarted(true);
          if (gameId) {
            logGameStart(gameId);
          }
        };

    // ✅ Move useEffect to the main component scope
        useEffect(() => {
            if (isGameComplete) {
                setTimeout(() => {
                    console.log("🎉 Triggering dialog...");
                    setOpenCongratsDialog(true);
                    setIsGameStarted(false);
                }, 500); // ✅ Added small delay for UI update
            }
        }, [isGameComplete]); // ✅ Only runs when `isGameComplete` changes

        useEffect(() => {
            console.log("👀 isGameComplete changed:", isGameComplete);
        }, [isGameComplete]);
  
        useEffect(() => {
            console.log("🎯 openCongratsDialog changed:", openCongratsDialog);
          }, [openCongratsDialog]);
          

    // 🔍 Function to get movie ID from movie title using Supabase cache or TMDb API
    const fetchMovieId = async (movieTitleOrId, currentActor = null) => {
        try {
            if (!isNaN(movieTitleOrId)) {
                console.log(`🎬 Input "${movieTitleOrId}" is already a movie ID.`);
                return { id: movieTitleOrId, poster: null };
            }

            const titleMatch = movieTitleOrId.match(/^(.*?)\s*\((\d{4})\)$/);
            let movieTitle = titleMatch ? titleMatch[1].trim() : movieTitleOrId.trim();
            let releaseYear = titleMatch ? titleMatch[2] : null;

            // Fetch from Supabase cache first
            let exactLabel = releaseYear ? `${movieTitle} (${releaseYear})` : movieTitle;

            let query = supabase
              .from('actor_movie_connections')
              .select('movie_id, movie_poster, movie_name')
              .eq('movie_name', exactLabel);

            if (currentActor) {
              query = query.eq('actor_name', currentActor);
            }

            const { data: cachedMovie, error: cacheError } = await query.limit(1);


            if (cacheError) {
              console.error("❌ Supabase cache error:", cacheError);
            }

            if (cachedMovie && cachedMovie.length > 0) {
              const cachedRow = cachedMovie[0];

              if (cachedRow.movie_poster) {
                console.log(`🗄️ Using fully cached movie for "${movieTitle}":`, cachedRow.movie_id);
                return {
                  id: cachedRow.movie_id,
                  poster: cachedRow.movie_poster?.startsWith("http")
                    ? cachedRow.movie_poster
                    : `https://image.tmdb.org/t/p/w500${cachedRow.movie_poster}`,
                  titleWithYear: cachedRow.movie_name
                };                
              } else {
                console.warn(`⚠️ Cached movie "${movieTitle}" is missing poster — will fallback to TMDb API.`);
              }
            }
            // If no cache hit, proceed with TMDb API
            console.log("🌍 No cache hit. Fetching from TMDb...");
    
            let url = `https://api.themoviedb.org/3/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(movieTitle)}`;
            if (releaseYear) {
                url += `&year=${releaseYear}`;
            }
    
            console.log("Fetching movie ID for:", movieTitle, releaseYear ? `(${releaseYear})` : "");
            const response = await fetch(url);
            const data = await response.json();
    
            if (!data.results || data.results.length === 0) {
                console.error(`❌ Movie not found.`);
                setError(`Movie not found.`);
                return null;
            }

            const boostedResults = isBreely
              ? [
                  ...data.results.filter(m => indianLanguages.has(m.original_language)),
                  ...data.results.filter(m => !indianLanguages.has(m.original_language))
                ]
              : data.results;
    
            // 🎯 Try matching by title + release year
            let exactMatches = boostedResults.filter(movie =>
              movie.title.toLowerCase() === movieTitle.toLowerCase() &&
              (!releaseYear || movie.release_date?.startsWith(releaseYear))
            );

            // 🧠 Disambiguate if multiple matches and you have an actor context
            let bestMatch;
            if (exactMatches.length === 1) {
              bestMatch = exactMatches[0];
            } else if (exactMatches.length > 1 && currentActor) {
              console.log(`🧠 Multiple exact matches found for "${movieTitle} (${releaseYear})". Disambiguating using actor: ${currentActor}`);
              bestMatch = await disambiguateByActor(exactMatches, currentActor);
            }            

            // Fallbacks
            if (!bestMatch) {
              bestMatch = boostedResults.find(movie =>
                movie.title.toLowerCase() === movieTitle.toLowerCase()
              ) || boostedResults.find(movie =>
                movie.release_date?.startsWith(releaseYear)
              ) || boostedResults[0];
            }

            // 🛡️ Defensive guard: still no valid movie
              if (!bestMatch?.id) {
                console.error("❌ No valid movie ID found after fallback and disambiguation.");
                setError("Movie not found.");
                return null;
              }
    
            console.log(`✅ Fetched movie ID for "${movieTitle}":`, bestMatch.id);
            return {
                id: bestMatch.id,
                poster: bestMatch.poster_path ? `https://image.tmdb.org/t/p/w500${bestMatch.poster_path}` : null,
                titleWithYear: `${bestMatch.title} (${bestMatch.release_date?.slice(0,4)})`
            };
        } catch (error) {
            console.error("❌ Error fetching movie ID:", error);
            setError("Failed to fetch movie ID.");
            return null;
        }
    };    

    const disambiguateByActor = async (movies, actorName) => {
      for (const movie of movies) {
        const creditsUrl = `https://api.themoviedb.org/3/movie/${movie.id}/credits?api_key=9fd3ab94d965f38b3b67d6dfde8651fe`;
        const creditsRes = await fetch(creditsUrl);
        const credits = await creditsRes.json();
    
        const found = credits.cast?.some(
          c => c.name?.toLowerCase() === actorName.toLowerCase()
        );
    
        if (found) {
          console.log("🎯 Disambiguated using actor:", actorName, "→", movie.title, movie.id);
          return movie;
        }
      }
    
      return null;
    };
    
    
    const fetchActorProfile = async (actorName) => {
        try {
            const url = `https://api.themoviedb.org/3/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(actorName)}`;
            const response = await fetch(url);
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                return `https://image.tmdb.org/t/p/w500${data.results[0].profile_path}`;
            } else {
                console.error(`❌ Actor "${actorName}" not found.`);
                return null;
            }
        } catch (error) {
            console.error("❌ Error fetching actor profile:", error);
            return null;
        }
    };

    const fetchActorProfileById = async (actorId) => {
        try {
          const url = `https://api.themoviedb.org/3/person/${actorId}?api_key=${TMDB_API_KEY}`;
          const response = await fetch(url);
          const data = await response.json();
      
          if (data?.profile_path) {
            return `https://image.tmdb.org/t/p/w500${data.profile_path}`;
          } else {
            console.warn(`⚠️ No profile image found for actor ID ${actorId}`);
            return null;
          }
        } catch (error) {
          console.error("❌ Error fetching actor profile by ID:", error);
          return null;
        }
      };      

      const fetchHintOptions = async (entry, index) => {
        try {
          const normalize = (str) =>
            str?.toLowerCase().replace(/[^\w\s]/g, "").trim();
          const usedNames = new Set(
            route.map((item) => normalize(item.name.replace(/\s*\(\d{4}\)$/, "")))
          );    

          const normalizedName = normalize(entry.name);

          // Only count as new hint if we haven't seen it yet
            if (!hintedItems.has(normalizedName)) {
              setHintCount((prev) => prev + 1);
              setHintedItems((prev) => new Set(prev).add(normalizedName));
            }
          
            if (entry.type === "start-movie") {
              const fetchTopActors = async (movieTitle) => {
                const movieData = await fetchMovieId(movieTitle);
                if (!movieData?.id) return [];
            
                const res = await fetch(
                  `https://api.themoviedb.org/3/movie/${movieData.id}/credits?api_key=${TMDB_API_KEY}`
                );
                const json = await res.json();
            
                return json.cast
                  .filter(actor => actor.known_for_department === "Acting")
                  .filter(actor => !usedNames.has(normalize(actor.name)))
                  .sort((a, b) => a.order - b.order || b.popularity - a.popularity)
                  .slice(0, 5)
                  .map(actor => ({
                    name: actor.name,
                    profile: actor.profile_path
                      ? `https://image.tmdb.org/t/p/w185${actor.profile_path}`
                      : null,
                  }));
              };
            
              const startActors = await fetchTopActors(startMovie);
              const endActors = await fetchTopActors(endMovie);
            
              setHintOptions([...startActors, ...endActors]);
              setHintSourceName(`${startMovie} & ${endMovie}`);
              setIsStartEndHint(true);
              setHintAnchorIndex(null); // special case
              setShowHintOverlay(true);
              return; // ✅ Prevent falling into other blocks       
          } else {
            setIsStartEndHint(false);
          }          
      
          if (entry.type === "movie") {
            const movieData = await fetchMovieId(entry.name);
            if (!movieData?.id) return;
      
            const res = await fetch(`https://api.themoviedb.org/3/movie/${movieData.id}/credits?api_key=${TMDB_API_KEY}`);
            const json = await res.json();
      
            const topActors = json.cast
            .filter(actor => actor.known_for_department === "Acting")
            .filter(actor => !usedNames.has(normalize(actor.name)))
            .sort((a, b) => {
              // Give priority to billing order first, then use popularity as a tiebreaker
              return a.order - b.order || b.popularity - a.popularity;
            })
            .slice(0, 5)
              .map(actor => ({
                name: actor.name,
                profile: actor.profile_path
                  ? `https://image.tmdb.org/t/p/w185${actor.profile_path}`
                  : null
              }));
      
            setHintOptions(topActors);
            setHintSourceName(entry.name); // ✅ Needed so title shows: "Actors in {endMovie}"
            setIsStartEndHint(false); // ✅ Optional, but keeps state consistent
            setHintAnchorIndex(index);
            setShowHintOverlay(true);
      
          } else if (entry.type === "actor") {
            let actorId = entry.id;
          
            if (!actorId) {
              console.warn("⚠️ Missing actor ID — falling back to name search.");
          
              // Fallback: search by actor name
              const res = await fetch(`https://api.themoviedb.org/3/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(entry.name)}`);
              const data = await res.json();
          
              if (!data.results || data.results.length === 0) {
                console.warn(`⚠️ No results found for actor name: ${entry.name}`);
                return;
              }
          
              const bestMatch = data.results.find(
                person => person.name.toLowerCase() === entry.name.toLowerCase()
              ) || data.results[0];
          
              actorId = bestMatch.id;
            }
          
            // ✅ Now safely use actorId for movie credits
            const creditsRes = await fetch(`https://api.themoviedb.org/3/person/${actorId}/movie_credits?api_key=${TMDB_API_KEY}`);
            const creditsData = await creditsRes.json();
          
            const filteredMovies = (creditsData.cast || [])
              .filter((movie) => {
                const normalizedMovie = normalize(movie.title);
                return !Array.from(usedNames).some(
                  (used) => normalize(used) === normalizedMovie
                );
              })
              .sort((a, b) => b.popularity - a.popularity)
              .slice(0, 5);
          
            const topMovies = filteredMovies.map((movie) => ({
              name: `${movie.title} (${movie.release_date?.slice(0, 4) || "N/A"})`,
              profile: movie.poster_path
                ? `https://image.tmdb.org/t/p/w185${movie.poster_path}`
                : null,
            }));
          
            setHintOptions(topMovies);
            setHintAnchorIndex(index);
            setShowHintOverlay(true);
          }          
        } catch (error) {
          console.error("❌ Error fetching hint:", error);
        }
      };      

    const gameComplete = async (endMovieEntry) => {
        const finalRoute = [...route, endMovieEntry];
        const finalSteps = finalRoute.length - 2;
        setFinalFrameCount(finalSteps);
      
        console.log("✅ Game complete via gameComplete()");
        console.log("📦 Route before saving:", finalRoute);
        console.log("🧮 Final frame count:", finalSteps);
        //console.log("🕵️‍♂️ isBreely:", isBreely);  // 👈 Add this

        setRoute(finalRoute);
        setIsGameComplete(true);
        setOpenCongratsDialog(true);
      
        saveGameData(startMovie, endMovie, finalSteps, true, "completed");
        updateUserStats();

        saveCompletedReel({
          userId,
          startMovie,
          endMovie,
          route: finalRoute,
          steps_taken: finalSteps,
          hintCount,
          rewindCount,
          source: isBreely ? "breely" : "reely"
        });

          // ✅ Stats icon color logic
          setIsTodayGameComplete(true);
          localStorage.setItem("isTodayGameComplete", "true");

          const totalReelsCompleted = await updateUserStats();

          const hasSeenInstaPrompt = localStorage.getItem("reelySeenInstaPrompt");

          if (!hasSeenInstaPrompt && totalReelsCompleted >= 1) {
            // ⏳ Delay showing prompt by 1.5s
            setTimeout(() => {
              setShowInstaPrompt(true);
            }, 1500);
          }      

          const { data: avgResult, error: avgError } = await supabase
          .rpc('get_average_steps', {
            start_movie_input: startMovie,
            end_movie_input: endMovie
          });
        
        if (avgError) {
          console.warn("⚠️ Error fetching average frame count:", avgError);
        } else {
          console.log("🧮 Supabase returned average frame count:", avgResult);
          if (avgResult !== null) {
            setAverageFrameCount(avgResult);
          } else {
            console.log("ℹ️ Not enough data to show average frame count yet.");
          }
        }  
        
        const { data: minResult, error: minError } = await supabase
        .rpc('get_min_steps', {
          start_movie_input: startMovie,
          end_movie_input: endMovie
        });

      if (minError) {
        console.warn("⚠️ Error fetching minimum frame count:", minError);
      } else {
        console.log("🥇 Supabase returned minimum frame count:", minResult);
        if (minResult !== null) {
          setMinimumFrameCount(minResult);
        }
      }

      };

      const checkActorInStartMovieCast = (actorInput) => {
        const normalizedInput = normalizeString(actorInput);
      
        if (!Array.isArray(startMovieCast)) {
          console.warn("⚠️ startMovieCast is missing or not an array.");
          return { found: false, actorId: null };
        }
      
        const match = startMovieCast.find((actor) => {
          const name = typeof actor === "string" ? actor : actor?.name;
          return normalizeString(name) === normalizedInput;
        });
      
        if (match) {
          return { found: true, actorId: match.id };
        } else {
          return { found: false, actorId: null };
        }
      };        
      
      const validateConnection = async () => {
        const start = performance.now();
        if (!currentMovie && !currentActor) {
          setError("Start with a valid movie or actor.");
          return;
        }
      
        const loadingTimeout = setTimeout(() => {
          setIsLoadingCast(true);
        }, 300); // show spinner only after 300ms
      
        setShowLongLoadMessage(false);
      
        const longLoadTimeout = setTimeout(() => {
          setShowLongLoadMessage(true);
        }, 3000); // show long message after 3s
      
        try {
          let movieId = "";
          let actorName = "";
          let resolvedActorImage = null;
          let moviePoster = null;  
      
          console.log("🔍 Current Game State BEFORE Processing:", {
            currentMovie,
            currentActor,
            userInput,
          });

          const isManualEntry = !selectedSuggestion;
      
          if (currentMovie) {
            actorName = userInput;          

            const lastMovieInRoute = route?.[route.length - 1];

            if (lastMovieInRoute?.id) {
              movieId = lastMovieInRoute.id; 
              moviePoster = lastMovieInRoute.poster;
            } else {
              const movieData = await fetchMovieId(currentMovie, currentActor);
              console.log("🎯 Fallback fetchMovieId result:", movieData);
              movieId = movieData?.id;
              moviePoster = movieData?.poster;
            }
            
              if (!movieId) {
                console.error("❌ Could not resolve movie ID for start movie.");
                setError("Invalid input. Please enter a valid movie or actor.");
                return;
              }
              setMoviePoster(moviePoster);
          

            const { found, actorId } = checkActorInStartMovieCast(userInput);

            if (currentMovie === startMovie && found) {
              console.log("⚡ Actor found in Supabase start_movie_cast!");

              if (actorId) {
                resolvedActorImage = await fetchActorProfileById(actorId);
                setActorImage(resolvedActorImage);
              }           
            } else {
              // ❗ Fallback to TMDB credits logic         
      
            try {
              const creditsRes = await fetch(
                `https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`
              );
              const creditsData = await creditsRes.json();
              const sortedCast = creditsData.cast.sort((a, b) => a.order - b.order);
              const normalized = (str) =>
                str?.toLowerCase().replace(/[^\w\s]/g, "").trim();              
      
              const matchedActor =
                sortedCast.find(
                  (actor) => normalized(actor.name) === normalized(actorName)
                ) ||
                sortedCast.find((actor) =>
                  normalized(actor.name).includes(normalized(actorName))
                ) ||
                sortedCast.find((actor) =>
                  normalized(actorName).includes(normalized(actor.name))
                );
      
              if (matchedActor?.id) {
                resolvedActorImage = await fetchActorProfileById(matchedActor.id);
                setActorImage(resolvedActorImage);
              } else {
                resolvedActorImage = await fetchActorProfile(actorName);
                setActorImage(resolvedActorImage);
              }
            } catch (err) {
              console.error("❌ Failed to fetch actor from credits:", err);
              resolvedActorImage = await fetchActorProfile(actorName);
              setActorImage(resolvedActorImage);
            }
            }
          } else if (currentActor) {
            actorName = currentActor;
            const movieData = await fetchMovieId(userInput, currentActor);
            movieId = movieData?.id;
      
            if (!movieId) throw new Error(`Movie not found.`);
      
            if (!moviePoster || currentMovie === startMovie) {
              setMoviePoster(movieData.poster);
            }
          }
      
          console.log("🚀 Preparing API Request:", { movieId, actorName });
      
          if (!movieId || !actorName) {
            throw new Error("Invalid input. Please enter a valid movie or actor.");
          }

          // 🧠 Check Supabase cache first
            const { data: cachedConnection, error: cacheError } = await supabase
            .from('actor_movie_connections')
            .select('*')
            .eq('movie_id', movieId)
            .eq('actor_name', actorName)  // use name for simplicity
            .limit(1);

            if (cacheError) console.error("❌ Supabase cache check error:", cacheError);

            if (cachedConnection && cachedConnection.length > 0) {
              console.log("✅ Connection found in Supabase cache:", cachedConnection);
              const cachedActorId = cachedConnection[0]?.actor_id || null;          

            // ✅ Pretend TMDb validated → jump to route update logic
            handleValidConnection({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId: cachedActorId, movieId, isManualEntry });
            
            const end = performance.now();
            try {
              LogRocket.log("[Latency] validateConnection (cache):", {
                duration: Math.round(end - start),
                result: "cache",
                input: userInput
              });              
            } catch (err) {
              console.warn("LogRocket.log failed:", err);
            }

            return;
            }
          
          console.log("🔍 Connection not found in Supabase cache — falling back to TMDb...");

          // 🧠 Check TMDB API for connection as fall back
          const url = `${backendUrl}/validate-connection?movieId=${movieId}&actorName=${actorName}`;
          console.log("🌍 API Request URL:", url);
      
          const response = await fetch(url);
          const data = await response.json();
      
          console.log("✅ API Response:", data);
      
          if (data.valid) {
            let actorImage = null;
            let moviePoster = null;

            // ✅ Fetch actor image (if actorId exists)
            if (data.actorId) {
              try {
                const actorDetailsRes = await fetch(`https://api.themoviedb.org/3/person/${data.actorId}?api_key=${TMDB_API_KEY}`);
                const actorDetails = await actorDetailsRes.json();
                actorImage = actorDetails?.profile_path ? `https://image.tmdb.org/t/p/w500${actorDetails.profile_path}` : null;
              } catch (err) {
                console.error("❌ Error fetching actor image:", err);
              }
            }

            // ✅ Fetch movie poster
            try {
              const movieDetailsRes = await fetch(`https://api.themoviedb.org/3/movie/${movieId}?api_key=${TMDB_API_KEY}`);
              const movieDetails = await movieDetailsRes.json();
              moviePoster = movieDetails?.poster_path ? `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}` : null;
            } catch (err) {
              console.error("❌ Error fetching movie poster:", err);
            }

            if (!isManualEntry) {
              // ✅ Insert connection into Supabase only if NOT manual
              supabase
              .from('actor_movie_connections')
              .insert([
                {
                  actor_name: actorName,
                  actor_id: data.actorId || null,
                  actor_image: actorImage || null,
                  movie_name: currentMovie || userInput,
                  movie_id: movieId,
                  movie_poster: moviePoster || null,
                  popularity: data.popularity || null,
                }
              ])
              .then(({ error }) => {
                if (error) {
                  console.error("❌ Supabase insert error (connections):", error);
                } else {
                  console.log("✅ New connection inserted into Supabase!");
                }
              });
          } else {
            console.log("⏩ Skipping Supabase insert for manual entry.");
          }

              const tmdbActorId = data.actorId || null;
            
              await handleValidConnection({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId: tmdbActorId, movieId, isManualEntry });

              const end = performance.now();
              try {
                LogRocket.log("[Latency] validateConnection (tmdb):", {
                  duration: Math.round(end - start),
                  result: "tmdb",
                  input: userInput
                });
              } catch (err) {
                console.warn("LogRocket.log failed:", err);
              }
              return;
          } else {
            setError("❌ No connection. Try again!");
            setHasInputError(true);
            setTimeout(() => setHasInputError(false), 500);
            if (
              isBreely &&
              currentMovie && // ✅ Means user is entering an actor
              !localStorage.getItem("breelySeenErrorTip")
            ) {
              setShowBreelyErrorTip(true);
              localStorage.setItem("breelySeenErrorTip", "true");
            }          
            const end = performance.now();
            LogRocket.log(`[Latency] validateConnection (no connection): ${Math.round(end - start)}ms`, {
              result: "invalid",
              input: userInput,
            });
          }
        } catch (error) {
          console.error("❌ Error validating connection:", error);
          setError(error.message || "Failed to validate connection.");
          const end = performance.now();
          LogRocket.log(`[Latency] validateConnection (error): ${Math.round(end - start)}ms`, {
            result: "error",
            input: userInput,
            message: error.message || "unknown"
          });
        } finally {
          clearTimeout(loadingTimeout);
          clearTimeout(longLoadTimeout);
          setIsLoadingCast(false);
        }
      };

      const handleValidConnection = async ({ movieId, actorName, resolvedActorImage, userInput, currentMovie, currentActor, actorId, isManualEntry }) => {
        const start = performance.now();
        const newEntry = { name: userInput, image: null, id: null};


        if (isManualEntry) {
          if (currentMovie) {
            // 🧽 Actor step
            const result = await polishManualActorWithinMovie(actorName, movieId);
            actorId = result.actorId;
            actorName = result.polishedName;
            resolvedActorImage = result.actorImage;
            newEntry.name = actorName;
            newEntry.id = actorId;
            console.log(`✅ Polished manual actor: ${actorName} (ID: ${actorId})`);
          } else if (currentActor) {
            // 🧽 Movie step
            const result = await polishManualMovie(userInput);
            newEntry.name = result.polishedName;
            newEntry.id = result.movieId;
            newEntry.image = result.moviePoster;
            console.log(`✅ Polished manual movie: ${result.polishedName} (ID: ${result.movieId})`);
          }
        }       
        
        if (currentMovie) {
          newEntry.id = actorId;
          newEntry.image = resolvedActorImage;
          logActorMovieUsage(userInput, "actor");
        } else if (currentActor) {
          const movieData = await fetchMovieId(userInput, currentActor);
          newEntry.id = movieData?.id;
          newEntry.image = movieData?.poster || null;
          logActorMovieUsage(userInput, "movie");
        
          if (currentMovie !== startMovie) {
            setMoviePoster(movieData.poster);
          }
        }   

            setError("");
            setUserInput("");
            setSearchTerm({ label: "" });
      
            console.log("✅ Valid Connection - Updating State:");
            console.log(`🔄 Previous Current Movie: ${currentMovie}`);
            console.log(`🔄 Previous Current Actor: ${currentActor}`);
            console.log(`🎭 New Input: ${userInput}`);

            if (currentMovie) {
              console.log("🔄 Switching to Actor:", userInput);
              setCurrentMovie("");
              setCurrentActor(isManualEntry ? newEntry.name : userInput);
            } else {
              console.log("🎬 Switching to Movie:", userInput);
              setCurrentMovie(isManualEntry ? newEntry.name : userInput);
              setCurrentActor("");
            }
      
            // ✅ Update route
            setTimeout(() => {
              setRoute((prevRoute) => {
                if (!prevRoute.some((entry) => entry.name === newEntry.name)) {
                  const isActor = currentMovie !== "";
                  const typedEntry = { ...newEntry, 
                    type: isActor ? "actor" : "movie",         
                  id: newEntry.id || null
                  };
                  const updatedRoute = [...prevRoute, typedEntry];
                  console.log("🔄 Updated Route:", updatedRoute);
                  return updatedRoute;
                }
                return prevRoute;
              });
            }, 100);
      
            const stripYear = (title) =>
              title?.replace(/\s*\(\d{4}\)$/, "").toLowerCase().trim();
      
            const isFinalMove = userInput === endMovie;
            const isBackupMatch =
              userInput?.toLowerCase().trim() === endMovie?.toLowerCase().trim();
            const isLooseMatch = stripYear(userInput) === stripYear(endMovie);

            const isCompletingGame = isFinalMove || isBackupMatch || isLooseMatch;
      
            if (isCompletingGame) {
              const endMovieEntry = {
                name: endMovie,
                image: endMoviePoster,
                id: endMovieId,
                type: "movie",
              };
      
              gameComplete(endMovieEntry);
              return; 
            } else {
              setsteps_taken((prev) => {
                const updated = prev + 1;
                saveGameData(startMovie, endMovie, updated, false);
                return updated;
              });
            }

            const end = performance.now();
            try {
              LogRocket.log(`[Latency] handleValidConnection: ${Math.round(end - start)}ms`);
            } catch (err) {
              console.warn("LogRocket.log failed:", err);
            }          
          };

          const polishManualActorWithinMovie = async (actorInput, movieId) => {
            try {
              const url = `https://api.themoviedb.org/3/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`;
              const response = await fetch(url);
              const data = await response.json();
          
              if (!data.cast || data.cast.length === 0) {
                console.warn(`⚠️ No cast data found for movie ID: ${movieId}`);
                return {
                  polishedName: actorInput,
                  actorId: null,
                  actorImage: null
                };
              }
          
              const normalizedInput = actorInput.trim().toLowerCase();
          
              const match = data.cast.find(
                (actor) => actor.name.trim().toLowerCase() === normalizedInput
              );
          
              if (!match) {
                console.warn(`⚠️ Actor "${actorInput}" not found in movie ID ${movieId} cast list.`);
                return {
                  polishedName: actorInput,
                  actorId: null,
                  actorImage: null
                };
              }
          
              return {
                polishedName: match.name,
                actorId: match.id,
                actorImage: match.profile_path
                  ? `https://image.tmdb.org/t/p/w500${match.profile_path}`
                  : null
              };
            } catch (error) {
              console.error("❌ Error polishing manual actor within movie:", error);
              return {
                polishedName: actorInput,
                actorId: null,
                actorImage: null
              };
            }
          };

          const polishManualMovie = async (movieInput) => {
            try {
              const movieData = await fetchMovieId(movieInput);
              if (movieData) {
                return {
                  polishedName: movieData.titleWithYear,
                  movieId: movieData.id,
                  moviePoster: movieData.poster
                };
              } else {
                console.warn(`⚠️ Movie "${movieInput}" not found.`);
                return {
                  polishedName: movieInput,
                  movieId: null,
                  moviePoster: null
                };
              }
            } catch (error) {
              console.error("❌ Error polishing manual movie:", error);
              return {
                polishedName: movieInput,
                movieId: null,
                moviePoster: null
              };
            }
          };

const dismissEndMovieTooltip = () => {
    setShowEndMovieTooltip(false);
    localStorage.setItem("seenEndMovieTooltip", "true");
  };

useEffect(() => {
const handleClickOutside = (event) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
    dismissEndMovieTooltip();
    }
};

if (showEndMovieTooltip) {
    document.addEventListener("mousedown", handleClickOutside);
}

return () => {
    document.removeEventListener("mousedown", handleClickOutside);
};
}, [showEndMovieTooltip]);
  

const handleEndMovieClick = async () => {
    if (!currentActor || !endMovie) return;
  
    try {
      const response = await fetch(
        `https://api.themoviedb.org/3/movie/${endMovieId}/credits?api_key=${TMDB_API_KEY}`
      );
      const data = await response.json();
      const cast = data.cast.map((c) => c.name.toLowerCase());
  
      if (cast.includes(currentActor.toLowerCase())) {
        const endMovieEntry = {
          name: endMovie,
          image: endMoviePoster,
          id: endMovieId,
          type: "movie",
        };
  
        gameComplete(endMovieEntry); // ✅ Replaces all the manual state + Supabase logic
      } else {
        setError(`❌ No connection. Try again!`);
      }
    } catch (err) {
      console.error("❌ Error checking end movie cast:", err);
      setError("Couldn't verify connection to end movie.");
    }
  };  

// Reset the game to clear everything
const resetGame = () => {
    setRoute([]);
    setCurrentMovie("");
    setCurrentActor("");
    setUserInput("");
    setError("");
    setIsGameStarted(false);
};

    // Undo last move (remove most recent entry from route)
    const undoLastMove = () => {
        if (route.length === 0) {
            setError("No moves to undo.");
            return;
        }
    
        console.log("🔄 Undoing last move. Previous Route:", route);
    
        const newRoute = [...route];
        newRoute.pop(); // Remove last entry
    
        // Reset user input and error
        setUserInput("");  
        setError("");
    
        if (newRoute.length === 0) {
            // Fully reset
            setCurrentMovie(startMovie);
            setCurrentActor("");
            setError("");
        } else {
            const lastEntry = newRoute[newRoute.length - 1];
        
            const lastName = typeof lastEntry === "string" ? lastEntry : lastEntry.name;
        
            if (newRoute.length === 1) {
                // 👈 Only start movie left
                setCurrentMovie(startMovie);
                setCurrentActor("");
            } else if (newRoute.length % 2 === 1) {
                // Odd → last was movie
                setCurrentMovie(lastName);
                setCurrentActor("");
            } else {
                // Even → last was actor
                setCurrentMovie("");
                setCurrentActor(lastName);
            }
        }            
    
        setRoute(newRoute);
        setsteps_taken((prevsteps_taken) => Math.max(prevsteps_taken - 1, 0)); // 🔢 Decrease step count, but never go below 0
        setRewindCount(prev => prev + 1); // ✅ Increment rewind count
    
        console.log("✅ Updated Route After Undo:", newRoute);
    };   

      const formatFrameCountAsEmoji = (count) => {
        const numberEmoji = ["0️⃣", "1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣"];
        return String(count)
          .split("")
          .map(d => numberEmoji[parseInt(d)] || d)
          .join("");
      };

      const formatHintCountAsEmoji = (count) => {
        const digitEmojiMap = {
          "0": "0️⃣",
          "1": "1️⃣",
          "2": "2️⃣",
          "3": "3️⃣",
          "4": "4️⃣",
          "5": "5️⃣",
          "6": "6️⃣",
          "7": "7️⃣",
          "8": "8️⃣",
          "9": "9️⃣"
        };
      
        return count.toString().split("").map(digit => digitEmojiMap[digit] || digit).join("");
      };      

      const generateShareText = (startMovie, endMovie, steps_taken, finalFrameCount, currentStreak, baseUrl, includeSpoilers, route, hintCount) => {
          const reelSummary = route.map(entry => entry.name || entry).join(" → ");
          const frameEmoji = formatFrameCountAsEmoji(finalFrameCount ?? steps_taken); 
          const hintEmoji = formatHintCountAsEmoji(hintCount);

        
        let text = `🎉 I just finished a ${isBreely ? "BReely" : "Reely"}!
        \n🎬 ${startMovie} → ${endMovie}
        \n${frameEmoji} Frames | ${hintEmoji} Hint${hintCount === 1 ? "" : "s"}`
        
        if (includeSpoilers) {
    text += `\n\n🎞️ My Reel:\n\n${reelSummary}`;
        }

        text += `\n\n🔥 Streak: ${currentStreak} day${currentStreak !== 1 ? "s" : ""}
        \n🍿 Play here:\n${baseUrl}`;

        return text;
      };
      
      const handleSMSShare = () => {
        if (typeof gtag === "function") {
          gtag("event", "share_reel_method", {
            event_category: "Engagement",
            event_label: "Shared via SMS",
            value: includeSpoilers ? 1 : 0, 
          });
        }

        const text = generateShareText(startMovie, endMovie, finalFrameCount, steps_taken, currentStreak, baseUrl, includeSpoilers, route, hintCount);
        const smsLink = `sms:&body=${encodeURIComponent(text)}`;
        window.location.href = smsLink;
        setOpenShareOptions(false);

        try {
            if (window.LogRocket && LogRocket.track) {
              LogRocket.track("Shared via SMS");
            }
          } catch (err) {
            console.warn("❌ LogRocket track failed:", err);
          }
      };
      
      const handleWhatsAppShare = () => {
        if (typeof gtag === "function") {
          gtag("event", "share_reel_method", {
            event_category: "Engagement",
            event_label: "Shared via WhatsApp",
            value: includeSpoilers ? 1 : 0, 
          });
        }

        const text = generateShareText(startMovie, endMovie, finalFrameCount, steps_taken, currentStreak, baseUrl, includeSpoilers, route, hintCount);
        const waLink = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(waLink, "_blank");
        setOpenShareOptions(false);

        try {
            if (window.LogRocket && LogRocket.track) {
              LogRocket.track("Shared via WhatsApp");
            }
          } catch (err) {
            console.warn("❌ LogRocket track failed:", err);
          }
      };

      const handleBlueskyShare = () => {
        // 🟩 Track click via Google Analytics
        if (typeof window.gtag === "function") {
          window.gtag("event", "share_reel_method", {
            event_category: "Engagement",
            event_label: "Bluesky Share Click",
            value: includeSpoilers ? 1 : 0,
          });
        
        const text = generateShareText(startMovie, endMovie, finalFrameCount, steps_taken, currentStreak, baseUrl, includeSpoilers, route, hintCount);
        const encodedMessage = encodeURIComponent(text);
        const blueskyUrl = `https://bsky.app/intent/compose?text=${encodedMessage}`;
        setOpenShareOptions(false);
    
        // Open Bluesky composer
        window.open(blueskyUrl, "_blank");


        try {
          if (window.LogRocket && LogRocket.track) {
            LogRocket.track("Shared via Bluesky");
          }
        } catch (err) {
          console.warn("❌ LogRocket track failed:", err);
        }
      }
      };
      
      
      const handleClipboardCopy = async () => {
        if (typeof gtag === "function") {
          gtag("event", "share_reel_method", {
            event_category: "Engagement",
            event_label: "Copied to Clipboard",
            value: includeSpoilers ? 1 : 0, 
          });
        }

        const text = generateShareText(startMovie, endMovie, finalFrameCount, steps_taken, currentStreak, baseUrl, includeSpoilers, route, hintCount);
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            alert("🔗 Copied to clipboard!");
          } else {
            alert("📎 Please tap and hold to copy:\n\n" + text);
          }
        } catch (err) {
          console.error("Clipboard error:", err);
        }
        setOpenShareOptions(false);
        
        try {
            if (window.LogRocket && LogRocket.track) {
              LogRocket.track("Shared via Copied to Clipboard");
            }
          } catch (err) {
            console.warn("❌ LogRocket track failed:", err);
          }
      };     
      
      const frameEmoji = formatFrameCountAsEmoji(finalFrameCount ?? steps_taken); 
      const hintEmoji = formatHintCountAsEmoji(hintCount);

      const fetchSuggestions = async (input, expectMovie) => {
        if (!input || input.trim().length < 2) {
          setSuggestions([]);
          return;
        }
      
        setLoading(true);

        const movieAllowlist = ["f1", "it", "up", "if", "us", "on", "go", "ma"];

        const normalizedInput = input.trim().toLowerCase();

        const isAllowedShortMovie = expectMovie && movieAllowlist.includes(normalizedInput);
    

        const normalizeAndSanitizeFTS = (input) => {
          return input
            .toLowerCase()
            .trim()
            .split(/\s+/) // split into words
            .map((word) => word.replace(/[()&|!:']/g, "")) // sanitize each word
            .filter(Boolean) // remove empty strings
            .join(" | ");
        };
        
        const queryType = expectMovie ? "movie" : "person";
        const safeQuery = normalizeAndSanitizeFTS(input);

        // console.log("🔍 Running .textSearch on Supabase cache:", { safeQuery, queryType });

        // 🎯 Early exit unless allowlisted short movie title
        // 🧪 Bypass cache for allowlisted short movie titles
        if (isAllowedShortMovie) {
          console.log("⚡ Bypassing cache for short allowlisted movie:", normalizedInput);
          const tmdbResults = await refreshTmdbSuggestionsAndReturn(normalizedInput, queryType);
          setSuggestions(tmdbResults);
          setLoading(false);
          return;
        }
      
        try {
          // 1️⃣ Fetch all cached suggestions for this type
          const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago
          
          /*
          console.log("🔍 Running .ilike cache lookup with filters:", {
            safeQuery,
            queryType,
          });
          */
          
          const { data: _cachedRows, error: cacheError } = await supabase
            .from('fetch_suggestions_cache')
            .select('results')
            .eq('type', queryType)
            .gt('updated_at', cutoff)
            .gt('query_length', 3)
            .order('updated_at', { ascending: false })
            .limit(2000);
       
          const cachedRows = _cachedRows?.flatMap(cachedRow => {
            return cachedRow?.results ?? [];
          })
          // console.log("🔍 Cache fetch results:", JSON.stringify(cachedRows));   

          if (cacheError) {
            console.error("❌ Supabase cache fetch error:", cacheError);
          }
      
          const cachedSuggestions = (_cachedRows || []).flatMap(row => {
            if (!row?.results || !Array.isArray(row.results)) return [];
            return row.results;
          });          

          // console.log(`🗄️ Supabase cache returned ${cachedSuggestions.length} suggestions`);
      
          // 🧹 Dedupe cached suggestions by label
          const seenLabels = new Set();
          const dedupedSuggestions = cachedSuggestions.filter(item => {
            if (!item.label) return false;
            const normalizedLabel = item.label.toLowerCase();
            if (seenLabels.has(normalizedLabel)) return false;
            seenLabels.add(normalizedLabel);
            return true;
          });

          // console.log(`🔍 Running Fuse.js on ${dedupedSuggestions.length} cache suggestions`);
      
          // 2️⃣ Fuse search across cache
          let fuseResults = [];
          if (dedupedSuggestions.length > 0) {
            const Fuse = (await import("fuse.js")).default;
            const fuse = new Fuse(dedupedSuggestions, {
              keys: ["label"],
              threshold: 0.3,
            });
            fuseResults = fuse.search(safeQuery);
          }
      
          const fuseMatchItems = fuseResults.map(r => r.item);  
          // console.log("🔍 Fuse results found:", fuseResults.length);
          
      
          // 3️⃣ Determine strategy: cache only, merge, or TMDb only

          if (fuseMatchItems.length >= 6) {
            // ✅ Use cache only
          
            const sortedResults = fuseMatchItems
              .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
              .slice(0, 10);
            setSuggestions(sortedResults);
            // console.log("✅ Cache match found. Suggestions:", sortedResults);
            setLoading(false);
            return;
          }
      
          if (fuseMatchItems.length > 0 && fuseMatchItems.length < 6) {
            // 🔁 Merge Fuse + TMDb
            console.log("🔁 Fuse match found but incomplete. Fetching TMDb to merge...");
            console.log("🔄 Cache suggestions:", fuseMatchItems.length, "| Expected minimum:", 6);
            const tmdbResults = await refreshTmdbSuggestionsAndReturn(safeQuery, queryType);
      
            const allResults = [...fuseMatchItems, ...(tmdbResults || [])];
      
            const seen = new Set();
            const deduped = allResults.filter(item => {
              const key = item.label?.toLowerCase();
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            });
      
            const sorted = deduped
              .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
              .slice(0, 10);
      
            setSuggestions(sorted);
            setLoading(false);
            return;
          }
      
          // 4️⃣ No good cache matches — pure fallback
          console.log("🌍 No cache match. Fetching TMDb...");
          await refreshTmdbSuggestionsAndCache(safeQuery, queryType, true);
      
        } catch (err) {
          console.error("❌ Error in fetchSuggestions:", err);
        } finally {
          setLoading(false);
        }
      };
      
      const refreshTmdbSuggestionsAndCache = async (query, type, updateUI = false) => {
        try {
          const endpoint = type === "movie" ? "movie" : "person";
          const url = `https://api.themoviedb.org/3/search/${endpoint}?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`;
          const response = await fetch(url);
          const data = await response.json();
      
          if (!data?.results) {
            console.warn("⚠️ No results from TMDb for:", query);
            return;
          }
      
          let suggestions = (data.results || []).map((item) => ({
            label: type === "movie"
              ? `${item.title}${item.release_date ? ` (${item.release_date.slice(0, 4)})` : ""}`
              : item.name,
            id: item.id,
            popularity: item.popularity || 0,
            type,
            genre_ids: item.genre_ids || [], // needed for filtering
            original_language: item.original_language || null
          }));
      
          // 🛡️ If fetching movies, filter out bad results
          if (type === "movie") {
            const excludedGenres = new Set([99, 10767]); // 99 = Documentary, 10767 = Talk Show
            const excludedKeywords = [
              "stand-up", "comedy special", "olympics", "super bowl",
              "halftime", "concert", "documentary", "special event", "at the BBC"
            ].map(k => k.toLowerCase());
      
            suggestions = suggestions.filter((suggestion) => {
              const titleLower = suggestion.label.toLowerCase();
              const titleWords = new Set(titleLower.split(/\s+/));
      
              const hasBadGenre = suggestion.genre_ids?.some(id => excludedGenres.has(id));
              const hasBadKeyword = Array.from(titleWords).some(word =>
                excludedKeywords.includes(word)
              );
      
              return !(hasBadGenre || hasBadKeyword);
            });
          }
      
          // 📦 Sort and trim
          const deduped = suggestions.filter((item, idx, self) =>
            idx === self.findIndex((t) => t.label === item.label)
          );
          const finalSuggestions = deduped
            .sort((a, b) => b.popularity - a.popularity)
            .slice(0, 10);

          if (updateUI) {
            setSuggestions(finalSuggestions);
          }

          if (finalSuggestions.length === 0) {
              // console.log("🚫 No results to cache for query:", query);
              return;
            }
            
          // 🧵 Background cache write via RPC
          supabase
          .rpc("increment_or_upsert_cache", {
            input_query: query,
            input_type: type,
            input_results: finalSuggestions,
          })
          .then(({ error }) => {
            if (error) {
              console.warn("❌ RPC cache write failed:", error);
            } else {
              console.log("✅ RPC cache write complete for:", query);
            }
          });
            
        } catch (err) {
          console.error("❌ TMDb refresh error:", err);
        }
      };

      const refreshTmdbSuggestionsAndReturn = async (query, type) => {
        try {
          const endpoint = type === "movie" ? "movie" : "person";
          const url = `https://api.themoviedb.org/3/search/${endpoint}?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`;
          const response = await fetch(url);
          const data = await response.json();
      
          if (!data?.results) {
            console.warn("⚠️ No results from TMDb for:", query);
            return [];
          }
      
          let suggestions = (data.results || []).map((item) => ({
            label: type === "movie"
              ? `${item.title}${item.release_date ? ` (${item.release_date.slice(0, 4)})` : ""}`
              : item.name,
            id: item.id,
            popularity: item.popularity || 0,
            type,
            genre_ids: item.genre_ids || [],
            original_language: item.original_language || null,
          }));
      
          // 🛡️ Filter if fetching movies
          if (type === "movie") {
            const excludedGenres = new Set([99, 10767]); // 99 = Documentary, 10767 = Talk Show
            const excludedKeywords = [
              "stand-up", "comedy special", "olympics", "super bowl",
              "halftime", "concert", "documentary", "special event"
            ].map(k => k.toLowerCase());
      
            suggestions = suggestions.filter((suggestion) => {
              const titleLower = suggestion.label.toLowerCase();
              const titleWords = new Set(titleLower.split(/\s+/));
      
              const hasBadGenre = suggestion.genre_ids?.some(id => excludedGenres.has(id));
              const hasBadKeyword = Array.from(titleWords).some(word =>
                excludedKeywords.includes(word)
              );
      
              return !(hasBadGenre || hasBadKeyword);
            });
          }
      
          // 📦 Dedupe suggestions internally
          const seenLabels = new Set();
          const deduped = suggestions.filter(item => {
            if (!item.label) return false;
            const normalizedLabel = item.label.toLowerCase();
            if (seenLabels.has(normalizedLabel)) return false;
            seenLabels.add(normalizedLabel);
            return true;
          });
      
          const finalSuggestions = deduped
            .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
            .slice(0, 10);
      
          // 🚫 Don't cache empty results
          if (finalSuggestions.length === 0) {
            // console.log("🚫 No results to cache for query:", query);
            return [];
          }

        // 🧵 Background cache write via RPC
        supabase
          .rpc("increment_or_upsert_cache", {
            input_query: query,
            input_type: type,
            input_results: finalSuggestions,
          })
          .then(({ error }) => {
            if (error) {
              console.warn("❌ RPC cache write failed:", error);
            } else {
              console.log("✅ RPC cache write complete for:", query);
            }
          });
      
          return finalSuggestions;
      
        } catch (err) {
          console.error("❌ TMDb fetch error in refreshTmdbSuggestionsAndReturn:", err);
          return [];
        }
      };      

    const saveGameData = async (startMovie, endMovie, steps_taken, isGameComplete, endResult = null) => {
        try {
            let userId = await getOrCreateUser(); // ✅ Ensure user exists
    
            if (!userId) {
                console.error("❌ No valid user ID found. Assigning temporary guest ID.");
                userId = uuidv4(); // ✅ Valid UUID format
            }
    
            // ✅ Always update steps_taken and end_result
            const updateData = {
                steps_taken: steps_taken,
                end_result: endResult,  // ✅ Log how the game ended
            };
    
            if (isGameComplete) {
                updateData.completed_at = new Date().toISOString(); // ✅ Log completion time
                updateData.end_result = "completed"; // ✅ Ensure completion is logged
            }
    
            const { data, error } = await supabase
                .from("Games")
                .update(updateData)
                .eq("user_id", userId)
                .order("created_at", { ascending: false }) // ✅ Update only the latest game
                .limit(1);
    
            if (error) throw error;
            console.log(`✅ Game data updated: ${isGameComplete ? "Game Finished!" : "Steps Updated"}`, data);
        } catch (error) {
            console.error("❌ Error saving game data:", error);
        }
    };    

    const logActorMovieUsage = async (name, type) => {
        try {
          const userId = await getOrCreateUser(); // Ensure the user ID exists
          if (!userId) return;
      
          // 1️⃣ Update user-specific usage
          const { error: userError } = await supabase.rpc('increment_user_actor_movie_usage', {
            input_user_id: userId,
            input_name: name,
            input_type: type
          });
          if (userError) throw userError;
      
          // 2️⃣ Update global usage
          const { error: globalError } = await supabase.rpc('increment_actor_movie_usage', {
            input_name: name,
            input_type: type
          });
          if (globalError) throw globalError;
      
          console.log(`✅ Logged usage for ${name} (${type}) by user ${userId}`);
        } catch (error) {
          console.error("❌ Error logging actor/movie usage:", error);
        }
      };      

      const saveCompletedReel = async ({
        userId,
        startMovie,
        endMovie,
        startMovieId,
        endMovieId,
        route,
        steps_taken,
        hintCount, 
        rewindCount,
        source
      }) => {
        try {
          const actorChain = route.filter(item => item.type === "actor").map(item => item.name);
          const movieChain = route.filter(item => item.type === "movie").map(item => item.name);
      
          const { error } = await supabase.from("completed_reels").insert([
            {
              user_id: userId,
              start_movie: startMovie,
              end_movie: endMovie,
              actor_chain: actorChain,
              movie_chain: movieChain,
              frame_count: steps_taken,
              hints_used: hintCount, 
              rewind_count: rewindCount,
              source: source || "reely" // fallback just in case
            }
          ]);
      
          if (error) {
            console.error("❌ Error saving completed reel:", error);
          } else {
            console.log("✅ Completed reel saved to Supabase");
          }
        } catch (err) {
          console.error("❌ Unexpected error saving reel:", err);
        }
      };
      

      const updateUserStats = async () => {
        const userId = await getOrCreateUser();
        const today = getTodayInPST();
        const ymd = getYesterdayInPST();
      
        // 👇 Check if user row exists in usersv2
        let { data: user, error: selectError } = await supabase
        .from("usersv2")
        .select("last_completed_date, current_streak, longest_streak, total_reels_completed")
        .eq("user_id", userId)
        .maybeSingle(); // <-- prevents .single() errors when no match

        if (!user) {
        console.warn("⚠️ No row found in usersv2 — creating new one...");
        const { error: insertError } = await supabase
            .from("usersv2")
            .insert([{ user_id: userId }]);

        if (insertError) {
            console.error("❌ Failed to create fallback user row:", insertError.message);
            return;
        }

        // Retry fetch after inserting
        const result = await supabase
            .from("usersv2")
            .select("last_completed_date, current_streak, longest_streak, total_reels_completed")
            .eq("user_id", userId)
            .single();

        user = result.data;
        }

      
        // 🌎 Normalize last_completed_date to PST for accurate comparison
        const lastCompleted = user.last_completed_date; // already in YYYY-MM-DD format

        let currentStreak = 1;
        let longestStreak = user.longest_streak || 0;
        let totalCompleted = (user.total_reels_completed || 0) + 1;

        console.log("📅 Comparison Debug", {
            raw: user.last_completed_date,
            lastCompleted,
            today,
            ymd,
          });

        if (lastCompleted === today) {
        currentStreak = user.current_streak || 1;
        longestStreak = Math.max(user.longest_streak || 0, currentStreak);
        } else if (lastCompleted === ymd) {
        currentStreak = (user.current_streak || 0) + 1;
        longestStreak = Math.max(user.longest_streak || 0, currentStreak);
        } else {
        currentStreak = 1;
        longestStreak = Math.max(user.longest_streak || 0, currentStreak);
        }
          
          console.log("🧠 Updating user stats:", {
            userId,
            totalCompleted,
            currentStreak,
            longestStreak,
            last_completed_date: today,
          });         
          
          if (!uuidValidate(userId)) {
            console.error("❌ Invalid UUID format detected:", userId);
            return;
          }     
          
          // ✅ Convert string UUID to a format Postgres accepts
          const coercedUUID = userId && typeof userId === "string" ? userId : String(userId);
          
          console.log("📬 Preparing update for user:", userId, "Type:", typeof userId);

          // Slight delay before update (sometimes helps with consistency on fresh inserts)
            await new Promise(resolve => setTimeout(resolve, 100));

      
          const { data: updatedRow, error: updateError } = await supabase
          .from("usersv2")
          .update({
            total_reels_completed: totalCompleted,
            current_streak: currentStreak,
            longest_streak: longestStreak,
            last_completed_date: today,
          })
          .eq("user_id", userId.toLowerCase())
          .select(); // <-- ✅ Show the updated row

          const totalReelsCompleted = updatedRow?.[0]?.total_reels_completed || 0;

      
          if (updateError) {
            console.error("❌ Supabase update failed:", updateError.message);
          } else if (!updatedRow || updatedRow.length === 0) {
            console.warn("⚠️ Update succeeded but no rows were affected — check if user_id is an exact UUID match.");
          } else {
            console.log("✅ Supabase update succeeded. Updated row:", updatedRow[0]);
          }

          await fetchUserStreak(); // ✅ Re-fetches accurate streak from DB
          return totalReelsCompleted;          // ✅ Return updated total reels completed

      };

      const fetchReelStats = async (userId) => {
        const { data, error } = await supabase
          .from("completed_reels")
          .select("frame_count")
          .eq("user_id", userId);
      
        if (error) {
          console.error("❌ Error fetching reel stats:", error.message);
          return null;
        }
      
        const frameCounts = data
          .map(entry => entry.frame_count)
          .filter(count => typeof count === "number"); // ✅ Safeguard nulls
      
        if (frameCounts.length === 0) return { avg: 0, min: 0, max: 0 };
      
        const sum = frameCounts.reduce((acc, val) => acc + val, 0);
        const avg = (sum / frameCounts.length).toFixed(2);
        const min = Math.min(...frameCounts);
        const max = Math.max(...frameCounts);
      
        return {
          avg: Number(avg),
          min,
          max,
        };
      };
      
      const fetchUserStats = async (userId) => {
        const { data, error } = await supabase
          .from("usersv2")
          .select("*")
          .eq("user_id", userId)
          .single();
      
        if (error) {
          console.error("❌ Error fetching user stats:", error.message);
          return null;
        }
      
        return data;
      };


      useEffect(() => {
        if (showStatsModal && userId) {
          fetchUserStats(userId).then((data) => {
            console.log("📊 userStats fetched:", data);
            setUserStats(data);
          });
          fetchReelStats(userId).then((data) => {
            console.log("🎞️ reelStats fetched:", data);
            setReelStats(data);
          });
        }
      }, [showStatsModal, userId]);
  
      useEffect(() => {
        if (isGameComplete && typeof gtag === "function") {
          gtag("event", "completed_reel", {
            event_category: "Game",
            event_label: "Reel completed",
            value: steps_taken || 0,
          });
        }
      }, [isGameComplete]); // ✅ only runs when game is marked complete       
      

    // 🎉 CONGRATS DIALOG CONTENT
    const renderCongratsDialog = () => (
        <Dialog
        open={openCongratsDialog}
        fullScreen // ⬅️ Forces full overlay (esp. helpful on mobile)
        onClose={() => setOpenCongratsDialog(false)}
        maxWidth="sm"
        fullWidth
        container={document.body} // ⬅️ Force dialog to render in visible container
        >
        <DialogTitle
            sx={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: "24px",
            textAlign: "center",
            paddingTop: "20px"
            }}
        >
            Reel Complete!
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", paddingBottom: "16px" }}>

              {/* 🎉 Congratulatory GIF */}
                <img 
                    src={dailyGifUrl}
                    alt="Congrats GIF"
                    style={{ 
                    maxWidth: "100%", 
                    height: "180px", 
                    objectFit: "cover", 
                    marginBottom: "16px", 
                    borderRadius: "8px" 
                    }}
                />

            <Typography
            variant="body1"
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "16px",
                marginBottom: "10px",
                zIndex: 9999, 
                position: "relative"
            }}
            >
                <strong>{startMovie}</strong> → <strong>{endMovie}</strong>
                <br />
                <span style={{ marginRight: "4px", fontWeight: "bold", fontSize: "20px" }}>{frameEmoji}</span> 
                <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "15px" }}>{finalFrameCount === 1 ? "Frame" : "Frames"}</span>

                <span style={{fontFamily: "'Montserrat', sans-serif", fontSize: "20px" }}> |</span>
                <span style={{ marginLeft: "8px", marginRight: "6px", fontSize: "20px" }}>{hintEmoji}</span>
                <span style={{ fontSize: "15px" }}>Hints</span>
                
            </Typography>

            {parsedAverage !== null && (
              <Typography 
                variant="body1" 
                style={{ fontSize: "12px", fontFamily: "'Montserrat'", marginTop: "-6px", marginBottom: "12px", fontStyle: "italic"}}
              >
                {/* <span style={{fontSize: "12px", fontFamily: "'Montserrat'", fontStyle: "normal", fontWeight: "bold"}}> Today's Reely</span> <br /> */}
                 Today's Average: <span style={{ fontSize: "12px"}}>{parsedAverage}</span><span style={{fontSize: "16px", fontFamily: "'Montserrat'"}}> ·</span> Fastest: {minimumFrameCount} 
              </Typography>
            )}

            {!isReelHidden ? (
            <Box sx={{ marginBottom: 2 }}>
            {/* 🎬 Display the route visually */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginBottom: "20px"
                }}
                >
                {route.map((entry, index) => (
                    <Chip
                    key={index}
                    label={entry.name || entry}
                    size="small"
                    avatar={
                        entry.image ? (
                        <img
                            src={entry.image}
                            alt={entry.name}
                            style={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            objectFit: "cover"
                            }}
                        />
                        ) : null
                    }
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "12px",
                    }}
                    />
                ))}
                </div>
              </Box>
            ) : null
            }

            {showInstaPrompt && (
              <div
                ref={instaPromptRef}
                style={{
                  position: "absolute",
                  top: "360px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#ffe6e6",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontFamily: "'Montserrat'",
                  fontSize: "12px",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                  width: "240px",
                  maxWidth: "none",
                  textAlign: "center",
                }}
              >
                <div style={{ marginBottom: "4px" }}>
                  <strong>Fastest reel. Posted Daily.</strong>
                  <br /><br />
                  <a
                    href="https://www.reddit.com/r/playreely/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3f51b5", textDecoration: "underline" }}
                  >
                    r/playreely 
                  </a>
                  {/*}
                  <a
                    href="https://www.instagram.com/playreely/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3f51b5", textDecoration: "underline" }}
                  >
                      IG: playreely@
                  </a>
                  */}
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: "10px",
                    color: "#666",
                  }}
                  onClick={handleDismissInstaPrompt}
                >
                  ✖
                </div>
              </div>
            )}

            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsReelHidden(!isReelHidden)}
              sx={{
                borderRadius: "12px",
                padding: "2px 8px",
                minHeight: "28px",
                fontSize: "10px",
                fontFamily: "'Montserrat', sans-serif",
                textTransform: "none",
                backgroundColor: "transparent",
                borderColor: "#ccc",
                boxShadow: "none",
                height: "32px",
                marginBottom: 2
              }}
            >
              {isReelHidden ? "SHOW REEL" : "HIDE REEL"}
            </Button>


            {/* Description (centered above buttons) 
                <Typography 
                variant="body2" 
                sx={{ 
                    fontFamily: "'Montserrat'", 
                    fontSize: "12px", 
                    color: "#333", 
                    mt: 2, 
                    mb: 1,
                    maxWidth: "90%",
                    margin: "0 auto",
                    textAlign: "center"
                }}
                >
                Think you can do better? Tap <em>Play Again</em> below.  
                </Typography>*/}

                {/* Buttons side-by-side */}
                <Box 
                sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    gap: 1.5,           // spacing between buttons
                    flexWrap: "wrap",   // allows wrapping on small screens
                    mt: 1
                }}
                >
                <Button
                    variant="contained"
                    onClick={() => {
                    // 📈 GA4 Event
                    if (typeof gtag === "function") {
                      gtag("event", "play_again_click", {
                        event_category: "Engagement",
                        event_label: "Clicked Play Again",
                      });
                    }
                    setOpenCongratsDialog(false);
                    fetchRandomMovies(userId).then(() => {
                        handleStartGame();
                    });                  
                    setIsGameStarted(true);  
                    }}
                    sx={{ fontFamily: "'Montserrat'", marginTop: "-6px" }}
                >
                    Play Again
                </Button>
                
                <Button
                variant="contained"
                onClick={() => {
                  setOpenShareOptions(true);
                  if (typeof gtag === "function") {
                    gtag("event", "share_button_click", {
                      event_category: "Engagement",
                      event_label: "Opened Share Your Reel dialog",
                    });
                  }
                }}
                sx={{ fontFamily: "'Montserrat'", marginTop: "-6px" }}
              >
                Share Your Reel
              </Button>
                </Box>

            {currentStreak !== null && (
                <Box
                    sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center", // 👈 centers horizontally
                    gap: "12px", // space between streak and countdown
                    marginTop: "16px",   
                    }}
                >
                    <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "12px",
                        color: "#333",
                    }}
                    >
                    🔥 <strong>{currentStreak}-day streak</strong>
                    </Typography>

                    <Typography
                    variant="body2"
                    sx={{
                        fontSize: "12px",
                        fontFamily: "Montserrat",
                        color: "#333",
                    }}
                    >
                    ⏳ Next {window.location.hostname.includes("breely") ? "BReely" : "Reely"}: {" "}
                      <span
                        style={{
                          display: "inline-block",
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: "12px",
                          width: "85px", // 🔒 fixed width to prevent jitter
                          textAlign: "left", // keep natural flow
                        }}
                      >
                        {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
                      </span>
                    </Typography>
                </Box>
                )}


                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "'Montserrat'",
                    fontSize: "12px",
                    color: "#666",
                    marginTop: "8px",
                    marginBottom: "12px",
                    textAlign: "center",
                    lineHeight: 1.5 // Optional: keeps vertical spacing tidy
                  }}
                >
                  {/* Text Line */}
                  <span
                    style={{
                      display: "inline-block",
                      marginBottom: "4px", // ✅ Raises the text line slightly above the icons
                      fontFamily: "'Montserrat'",
                      fontSize: "12px",
                      color: "#666"
                    }}
                  >
                    Fastest reel posted daily on
                  </span>

                  {/* Reddit Icon */}
                  <a
                    href="https://www.reddit.com/r/playreely"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                      marginLeft: "6px",
                      marginRight: "4px"
                    }}
                  >
                    <img
                      src="/reddit_logo.png"
                      alt="Reddit"
                      style={{
                        width: "18px",
                        height: "18px",
                        verticalAlign: "middle"
                      }}
                    />
                  </a>

                  {/* Instagram Icon */}
                  <a
                    href="https://instagram.com/playreely"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      textDecoration: "none",
                      marginLeft: "4px"
                    }}
                  >
                    <img
                      src="/instagram_icon.png"
                      alt="Instagram"
                      style={{
                        width: "18px",
                        height: "18px",
                        verticalAlign: "middle"
                      }}
                    />
                  </a>

                  <br />

                  {/* Feedback line */}
                  {window.location.hostname.includes("breely") ? "BReely" : "Reely"} is in beta —{" "}
                  <a
                    href="https://forms.gle/2S2pVvDajVmX7KWJ7"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3f51b5", textDecoration: "underline" }}
                  >
                    share feedback
                  </a>
                  !
                </Typography>


        </DialogContent>
        </Dialog>
    );

    const renderHowToPlayDialog = () => (
        <Dialog open={openHowToPlay} onClose={() => setOpenHowToPlay(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Montserrat'", textAlign: "center" }}>How to Play</DialogTitle>
        <DialogContent sx={{ fontFamily: "'Montserrat'", fontSize: "14px" }}>
            <p>
            Create a <em>reel</em> to connect the <strong>start movie</strong> to the <strong>end movie</strong> by linking actors and movies.
            </p>
            <p>Example reel from <em>{isBreely ? "Lagaan" : "Titanic"}</em> to <em>{isBreely ? "Krrish" : "Elf"}</em>:</p>
            <div
                style={{
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    whiteSpace: "nowrap",
                    padding: "10px 0",
                    width: "100%",
                }}
                >
                <div
                    style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    minWidth: "max-content",
                    }}
                >
                 {(isBreely
                  ? [
                      { name: "Lagaan", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/yNX9lFRAFeNLNRIXdqZK9gYrYKa.jpg" },
                      { name: "Aamir Khan", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/bI3yxh4FzKL2hHYS4nbzcDMWtZs.jpg" },
                      { name: "3 Idiots", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/66A9MqXOyVFCssoloscw79z8Tew.jpg" },
                      { name: "Kareena Kapoor", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/pJZJJ93NwJq3kb3RWtaZBYVga1x.jpg" },
                      { name: "Kabhi Khushi Kabhie Gham", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/12BvYKueY0eruZuTYIextEZaCC.jpg" },
                      { name: "Hrithik Roshan", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/upKrdABAMK7jZevWAoPYI24iKlR.jpg" },
                      { name: "Krrish", image: "https://media.themoviedb.org/t/p/w300_and_h450_bestv2/Apd4UKesyTmpDutEjNVHP45kvBi.jpg" },
                  ]
                  : [
                      { name: "Titanic", image: "https://image.tmdb.org/t/p/w500/9xjZS2rlVxm8SFx8kPC3aIGCOYQ.jpg" },
                      { name: "Leonardo DiCaprio", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/ts9l18VkDSooRGDWIeQegNVHciC.jpg" },
                      { name: "Inception", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/ljsZTbVsrQSqZgWeep2B1QiDKuh.jpg" },
                      { name: "Joseph Gordon-Levitt", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/fm2vDPCzT2185e8qIt8RLvDx44U.jpg" },
                      { name: "500 Days of Summer", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/qXAuQ9hF30sQRsXf40OfRVl0MJZ.jpg" },
                      { name: "Zooey Deschanel", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/30KQyjsXfrdm4Dcori7bDFTg9Le.jpg" },
                      { name: "Elf", image: "https://media.themoviedb.org/t/p/w600_and_h900_bestv2/oOleziEempUPu96jkGs0Pj6tKxj.jpg" }
                  ]
                ).map((entry, index, arr) => (
                    <React.Fragment key={index}>
                        <Card
                        sx={{
                            width: 80,
                            height: 140,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                        >
                        <CardMedia
                            component="img"
                            image={entry.image}
                            alt={entry.name}
                            sx={{ height: 100, width: "100%", objectFit: "cover" }}
                        />
                        <Box sx={{ padding: "4px", textAlign: "center" }}>
                            <Typography
                            variant="body2"
                            sx={{
                                fontSize: "10px",
                                fontFamily: "'Montserrat'",
                                lineHeight: 1.2,
                                whiteSpace: "normal",
                            }}
                            >
                            {entry.name}
                            </Typography>
                        </Box>
                        </Card>
                        {index < arr.length - 1 && (
                        <Typography
                            variant="h5"
                            sx={{
                            margin: "0 6px",
                            display: "inline-block",
                            alignSelf: "center",
                            }}
                        >
                            →
                        </Typography>
                        )}
                    </React.Fragment>
                    ))}
                </div>
                </div>
            <p>
            You write the script! Build your reel in the fewest frames... or challenge yourself (e.g. choose no stars, all thrillers, only actresses).
            </p>
            <Button variant="contained" fullWidth onClick={() => setOpenHowToPlay(false)} sx={{ mt: 2 }}>
            Got it!
            </Button>
        </DialogContent>
        </Dialog>
    );

    const renderShareDialog = () => {
    
      return (
        <Dialog open={openShareOptions} onClose={() => setOpenShareOptions(false)}>
          <DialogTitle 
            sx={{ marginBottom: 2, fontSize: "20px", fontFamily: "'Montserrat'", fontWeight: "bold" }}
          >
          <span style={{ marginRight: "10px" }}>📤</span>{isBreely ? "Share your BReely" : "Share your Reely"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ marginBottom: 2, fontSize: "14px", fontFamily: "'Montserrat'" }}>
              Choose how you'd like to share:
            </Typography >
            <Stack spacing={2}>
              <Button variant="contained" onClick={handleSMSShare}>📱 Messages</Button>
              <Button variant="contained" onClick={handleWhatsAppShare}>🟢 WhatsApp</Button>
              <Button variant="contained" onClick={handleBlueskyShare}>🦋 Bluesky</Button>
              <Button variant="contained" onClick={handleClipboardCopy}>📋 Copy to Clipboard</Button>
            </Stack>
            <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSpoilers}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIncludeSpoilers(checked);

                      if (typeof gtag === "function") {
                        gtag("event", "toggle_include_spoilers", {
                          event_category: "Engagement",
                          event_label: checked ? "Enabled Spoilers" : "Disabled Spoilers",
                        });
                      }
                    }}
                    color="primary"
                  />

                }
                label={
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontFamily: "'Montserrat'",
                      marginLeft: 1,
                    }}
                  >
                    Include your reel (spoiler alert!)
                  </Typography>
                }
                sx={{ marginTop: 2 }}
              />
          </DialogContent>
        </Dialog>
      );
    };   

    const renderMainGame = () => (

        <div style={{ textAlign: "center", padding: "10px", minHeight: "100svh", paddingBottom: "200px" }}>
        <Typography 
              variant="h3" 
              className="reely-title" 
              style={{ fontFamily: "'Oswald', sans-serif", fontWeight: "bold !important" }}
          >
              {window.location.hostname.includes("breely") ? "BREELY" : "REELY"}
          </Typography>

          <IconButton onClick={() => setShowStatsModal(true)} 
            style={{ 
              position: "absolute",
              color: isTodayGameComplete ? "#228b37" : "black", 
              top: 10, 
              right: 10 
              }}>
            <BarChart2 size={20} />
            </IconButton>

            <StatsModal
                open={showStatsModal}
                onClose={() => setShowStatsModal(false)}
                userId={userId}
                userStats={userStats}
                reelStats={reelStats}
                />


          <div style={{ textAlign: "center", marginBottom: "10px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              <Typography 
              variant="h6"
              style={{ fontFamily: "'Montserrat'", fontSize: "14px", margin: 0 }}
              >
              Craft a 'reel' linking the two movies.
              </Typography>
              <InfoOutlinedIcon
              style={{ fontFamily: "'Oswald', sans-serif", fontSize: "18px", cursor: "pointer" }}
              onClick={() => setOpenHowToPlay(true)}
              titleAccess="How to Play"
              />
          </div>
      </div>

    {!isGameStarted && (
      <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          sx={{ width: "100%", margin: "10px 0" }}
          >
          {/* Start Movie */}
          <Box display="flex" flexDirection="column" alignItems="center" maxWidth="130px" flexShrink={0}>
              <img
              src={startMoviePoster}
              alt={startMovie}
              style={{
                  width: "100%",
                  maxHeight: "180px",
                  height: "auto",
                  borderRadius: "5px",
                  objectFit: "contain",
                  display: "block"
              }}
              />
                <Box
                  sx={{
                    position: "relative", // ✅ needed for absolute positioning of emoji
                    width: "100%",
                    textAlign: "center",
                    paddingX: 1
                  }}
                >
              <Typography
              variant="subtitle1"
              mt={1}
              style={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  minHeight: "32px",
                  textAlign: "center",
                  lineHeight: "1.3"
              }}
              >
              {startMovie}
              </Typography>
               {/* 🔍 Show only if game started and no actors/movies yet */}
                {isGameStarted && route.length === 1 && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchHintOptions({ name: startMovie, type: "start-movie" }, -1);
                    }}
                    style={{
                      position: "absolute",
                      bottom: "-4px",  // ⬇️ slightly lower
                      right: "8px",    // ⬅️ moves it left (negative value)
                      backgroundColor: "#81a6f0", 
                      // border: "1px solid #f0c36d", // ✨ thinner border
                      borderRadius: "50%",
                      padding: "0.2px", // 🔧 shrink padding to fit the smaller emoji
                      fontSize: "10px", // ✅ your chosen emoji size
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                      zIndex: 2
                    }}
                    title="Need a hint?"
                  >
                    💡
                  </span>
                )}
              </Box>
          </Box>

          {/* Arrow */}
          <Box display="flex" justifyContent="center" alignItems="center" height="180px">
              <Typography
              variant="h5"
              sx={{
                  fontFamily: "'Oswald'",
                  fontSize: "56px",
                  lineHeight: "1"
              }}
              >
              →
              </Typography>
          </Box>

          {/* End Movie */}
            <Box display="flex" flexDirection="column" alignItems="center" maxWidth="130px" flexShrink={0}>
              <img
              src={endMoviePoster}
              alt={endMovie}
              style={{
                  width: "100%",
                  maxHeight: "180px",
                  height: "auto",
                  borderRadius: "5px",
                  objectFit: "contain",
                  display: "block"
              }}
              />
              <Typography
              variant="subtitle1"
              mt={1}
              style={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  minHeight: "32px",
                  textAlign: "center",
                  lineHeight: "1.3"
              }}
              >
              {endMovie}
              </Typography>
            </Box>
        </Box>
    )}

{!isGameStarted ? (
  <Button
    onClick={() => {
      if (typeof gtag === "function") {
        gtag('event', 'start_game', {
          event_category: 'Game',
          event_label: 'Initial Start Button Clicked',
          value: 1
        });
      }
      handleStartGame(); // ✅ this actually calls it
    }}
    variant="contained"
    style={{ marginTop: "20px", fontFamily: "'Montserrat'" }}
  >
    Start Game
  </Button>
) : (
<>

<div >

   {/*EndMovieTooltip - anchored beside "Frames" */}
  {showEndMovieTooltip && (
    <div
      style={{
        position: "absolute",
        top: "130px", // push it down from the top
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#ffe6e6",
        borderRadius: "8px",
        padding: "16px",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
        fontSize: "14px",
        fontFamily: "'Montserrat'",
        width: "300px",
        zIndex: 9999,
        whiteSpace: "normal",
        textAlign: "center",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontfamily: "'Montserrat'", textAlign: "center" }}>
        <div style={{ flex: 1, textAlign: "center" }}>
          <strong>Pro Tip</strong>
         </div>
        <span style={{ cursor: "pointer" }} onClick={dismissEndMovieTooltip}>✕</span>
      </div>
      <div style={{ marginTop: "6px" }}>
        Tap the End Movie to see if your last actor finishes your reel!
      </div>
    </div>
  )}
</div>


  {/* Hint ToolTip - anchored beside "Frames" */}
    {showHintTooltip && isGameStarted && route.length === 1 && (
      <div
        ref={hintTooltipRef} // ✅ add here
          style={{
            position: "absolute",
            top: "130px", // push it down from the top
            left: "50%",
            transform: "translateX(-50%)",            
            right: "0px",
            backgroundColor: "#ffe6e6",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "8px 12px",
            fontFamily: "'Montserrat'",
            fontSize: "12px",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
            zIndex: 1000,
            maxWidth: "150px",
            textAlign: "center",
            pointerEvents: tooltipInteractive ? "auto" : "none",
        }}
      >
        <div style={{ marginBottom: "4px", alignItems: "normal", fontfamily: "'Montserrat'" }}>
          <strong>Need inspiration?</strong>
          <br /><br />
          Click the <span style={{
            position: "relative",    // ✅ use relative inside tooltip (no absolute here)
            backgroundColor: "#81a6f0",
            borderRadius: "50%",
            padding: "0.2px",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "default",        // No click needed inside tooltip
            userSelect: "none",
            display: "inline-block",  // ✅ keeps it inline
          }}>
            💡
          </span> for hints!

        </div>
        <div
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            cursor: "pointer",
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "10px",
            color: "#666",
            fontfamily: "'Montserrat'"
          }}
          onClick={handleDismissHintTooltip}
        >
          ✖
        </div>
      </div>
    )}

  {showBreelyErrorTip && (
    <div
      style={{
        position: "fixed",
        top: "12px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#ffe6e6",
        borderRadius: "8px",
        padding: "16px",
        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
        fontSize: "14px",
        fontFamily: "'Montserrat'",
        width: "300px",
        zIndex: 9999,
        textAlign: "left"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>Not seeing a connection?</strong>
        <span
          style={{ cursor: "pointer", fontWeight: "bold" }}
          onClick={() => setShowBreelyErrorTip(false)}
        >
          ✕
        </span>
      </div>
      <div style={{ marginTop: "10px" }}>
        Make sure the name is spelled correctly! Amir Khan ≠ Aamir Khan.
      </div>
    </div>
  )}


<div ref={topContainerRef}>
<div style={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            gap: "4px", 
            marginBottom: isGameStarted ? "6px" : "6px"  // ⬅️ adjust spacing after game starts
            }}>
      {!isGameStarted && (
        <Typography 
          variant="subtitle2" 
          style={{ 
            fontSize: "12px", 
            fontFamily: "'Montserrat'", 
          }}
        >
          {formatTodayDate()}
        </Typography>
      )}

      {/* ✅ Moved this outside the block above */}
      {isGameStarted && (
        <Typography 
          variant="body1" 
          style={{ 
            fontSize: "12px", 
            fontFamily: "'Montserrat'", 
          }}
        >
          Frames: {steps_taken} | Hints: {hintCount}
        </Typography>
      )}
    </div>


{/* Route Display */}
    <div
    style={{
      overflowX: "auto",
      scrollBehavior: "smooth",
      whiteSpace: "nowrap",
      paddingTop: "2px",
      paddingBottom: "2px",
      marginTop: "0px",
      marginBottom: "20px",
      width: "100%",
      maxWidth: "100vw",
    }}
    >
    <div
        style={{
        display: "inline-flex",         // ✅ Key: inline-flex instead of flex
        alignItems: "center",
        gap: "10px",
        minWidth: "max-content",       // ✅ Forces children to stay in same line
        }}
    >
              {route.map((entry, index) => {
                  const isLast = index === route.length - 1;
                return(
                  <React.Fragment key={index}>
                    {/* ✅ Add an arrow to the right for all but the last entry */}
                    {index > 0 && (
                        <Typography
                        variant="h5"
                        style={{
                            margin: "0 10px",
                            display: "inline-block",
                            alignItems: "center"
                        }}
                        >
                        →
                        </Typography>
                    )}     
                       <div>
                     <Card
                     sx={{
                       width: 100,
                       height: 170,
                       display: "flex",
                       flexDirection: "column",
                       alignItems: "center",
                       borderRadius: "8px",
                       overflow: "hidden",
                       paddingBottom: "4px", // adds balance under text
                       textAlign: "center"
                     }}
                   >
                     {entry.image && (
                       <CardMedia
                         component="img"
                         image={entry.image}
                         alt={entry.name}
                         sx={{
                           height: 120,
                           width: "100%",
                           objectFit: "cover"
                         }}
                       />
                     )}
                     <Box
                       sx={{
                         height: 50,
                         width: "100%",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         paddingX: 1,
                         position: "relative" 
                       }}
                     >
                       <Typography
                         variant="body2"
                         sx={{
                           fontFamily: "'Montserrat', sans-serif",
                           fontSize: "12px",
                           lineHeight: 1.3,
                           display: "-webkit-box",
                           WebkitBoxOrient: "vertical",
                           WebkitLineClamp: 2,
                           overflow: "hidden",
                           textOverflow: "ellipsis",
                           wordWrap: "break-word",
                           whiteSpace: "normal",
                           maxHeight: "3em",
                            textAlign: "center"
                         }}
                       >
                         {entry.name}
                       </Typography>
                       {/* 🔍 Current entry gets hint icon */}
                        {isGameStarted && isLast && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchHintOptions(entry, index);
                            }}
                            style={{
                              position: "absolute",
                              bottom: "-4px",  // ⬇️ slightly lower
                              right: "8px",    // ⬅️ moves it left (negative value)
                              backgroundColor: "#81a6f0", 
                              borderRadius: "50%",
                              padding: "0.2px", // 🔧 shrink padding to fit the smaller emoji
                              fontSize: "10px", // ✅ your chosen emoji size
                              fontWeight: "bold",
                              cursor: "pointer",
                              userSelect: "none",
                              zIndex: 2
                            }}
                            title="Need a hint?"
                          >
                            💡
                          </span>
                        )}
                     </Box>
                   </Card>  
                   </div>            
                  </React.Fragment>
              );
              })}
            {/* Dotted bridge between last entry and end movie */}
                {route.length > 0 && (
                <Typography
                    variant="h5"
                    sx={{
                    margin: "0 10px",
                    display: "inline-block",
                    alignItems: "center",
                    lineHeight: "1",          // ⬅️ helps vertical centering
                    fontFamily: "'Oswald'",   // ⬅️ match arrow styling
                    fontWeight: "400",        // ⬅️ prevents fuzzy text
                    color: "#999",            // ⬅️ optional for faint effect
                    }}
                >
                    • • •
                </Typography>
                )}
            <Card
                ref={lastEntryRef}
                onClick={handleEndMovieClick}
                sx={{
                    width: 100,
                    height: 170,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: "8px",
                    overflow: "hidden",
                    paddingBottom: "4px",
                    textAlign: "center",
                    opacity: 0.4, // 👻 Makes it faint
                    cursor: "pointer", // 🖱️ Indicates it's clickable
                    transition: "opacity 0.2s",
                    "&:hover": {
                    opacity: 0.6
                    }
                }}
                >
                <CardMedia
                    component="img"
                    image={endMoviePoster}
                    alt={endMovie}
                    sx={{
                    height: 120,
                    width: "100%",
                    objectFit: "cover"
                    }}
                />
                <Box
                    sx={{
                    height: 50,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingX: 1,
                    position: "relative"
                    }}
                >
                    <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "12px",
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                        maxHeight: "3em", // Limits to 2 lines visually
                        textAlign: "center",
                    }}
                    >
                    {endMovie}
                    </Typography>
                {/* 🔍 Hint Button */}
                {isGameStarted && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation(); // prevent triggering gameComplete
                      fetchHintOptions({ name: endMovie, type: "movie" }, -1);
                    }}
                    style={{
                      position: "absolute",
                      bottom: "-4px",  // ⬇️ slightly lower
                      right: "8px",    // ⬅️ moves it left (negative value)
                      backgroundColor: "#81a6f0", 
                      // border: "1px solid #f0c36d", // ✨ thinner border
                      borderRadius: "50%",
                      padding: "0.2px", // 🔧 shrink padding to fit the smaller emoji
                      fontSize: "10px", // ✅ your chosen emoji size
                      fontWeight: "bold",
                      cursor: "pointer",
                      userSelect: "none",
                      zIndex: 2
                    }}
                    title="Need a hint?"
                  >
                    💡
                  </span>
                )}
                </Box>
            </Card>
                </div>
          </div>
          </div>
          <>

          {showHintOverlay && hintOptions.length > 0 && (
              <div
                className="hint-overlay"
                style={{
                  position: "absolute",
                  top: "20%",
                  left: "0",                    // align to left edge
                  right: "0",                   // stretch to right edge
                  margin: "0 auto",             // center horizontally
                  maxWidth: "95vw",             // prevent full-bleed on tiny screens
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  zIndex: 9999,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                  fontFamily: "'Montserrat'"
                }}
              >{/* Dismiss hint 
                <div style={{ textAlign: "right" }}>
                  <Button size="small" onClick={() => setShowHintOverlay(false)}>X</Button>
                </div>*/}
                <Typography variant="subtitle2" sx={{ fontSize: "18px", fontFamily: "'Montserrat'", marginBottom: "8px" }}>
                  {isStartEndHint ? (
                    <>Actors in <em>{startMovie}"</em> and <em>{endMovie}</em></>
                  ) : hintAnchorIndex !== null && route[hintAnchorIndex]?.type === "movie" ? (
                    <>Actors in <em>{route[hintAnchorIndex]?.name}</em></>
                  ) : hintAnchorIndex !== null && route[hintAnchorIndex]?.type === "actor" ? (
                    <>Movies with {route[hintAnchorIndex]?.name}</>
                  ) : hintSourceName ? (
                    <>Actors in <em>{hintSourceName}</em></> // ✅ fallback for endMovie hints
                  ) : null}
                </Typography>

                <Stack spacing={1}>
                  {hintOptions.map((actor, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setUserInput(actor.name);
                        setSearchTerm({ label: actor.name });
                        setShowHintOverlay(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontFamily: "'Montserrat'",
                        padding: "6px 4px",
                        borderRadius: "6px",
                        transition: "background-color 0.2s",
                        color: "#333"
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      {actor.profile && (
                        <img
                          src={actor.profile}
                          alt={actor.name}
                          style={{ width: "36px", height: "36px", borderRadius: "50%" }}
                        />
                      )}
                      <span>{actor.name}</span>
                    </div>
                  ))}
                </Stack>
              </div>
            )}



    {/* Error Message Rendering */}

    {error && 
      <Typography
          color="error"
          style={{ 
              marginBottom: "8px",      // 👈 Reduce bottom spacing
              marginTop: "-4px",         // 👈 Add top spacing so it floats a bit
              fontFamily: "'Montserrat'", 
              fontSize: "12px", 
              position: "relative",     // 👈 Optional to help control spacing
              zIndex: 1                 // 👈 Ensure it's above any background artifacts
          }}
      >
          {error}
          
    </Typography>
    }

    {/* Loading Spinner Icon & Message */}
    {isLoadingCast && (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10px" }}>
        <div className="spinner" />
        {showLongLoadMessage && (
        <Typography variant="body2" sx={{ marginTop: "8px", fontFamily: "'Montserrat'", fontSize: "12px", color: "#999" }}>
            Production delays - hang tight!
        </Typography>
        )}
    </div>
    )}

        <div style={{ marginTop: "15px", marginBottom: "15px" }}>
          <Autocomplete
            freeSolo
            options={suggestions}
            value={searchTerm || ""}
            getOptionLabel={(option) => option.label}
            filterOptions={(x) => x}
            onInputChange={(event, newInput, reason) => {
              const safeInput = newInput ?? "";
              setSearchTerm({ label: safeInput });
              setUserInput(safeInput);
              if (reason === "input" || reason === "clear") {
                setSelectedSuggestion(null);
              }
              debouncedFetchSuggestions(safeInput, currentActor !== "");
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedSuggestion(newValue.label);
              }
            }}
            loading={loading}
            loadingText={<span style={{ fontStyle: "italic", fontSize: "14px", fontFamily: "'Montserrat'"}}>Loading...</span>}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <li key={option.id || option.label} {...rest} className="autocompleteOption">
                  {option.label}
                </li>
              );
            }}                     
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  currentMovie
                    ? `Enter an actor who appears in ${currentMovie}`
                    : `Enter a movie in which ${currentActor} appears`
                }
                variant="outlined"
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    validateConnection();
                  }
                }}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                
                // ✅ Style the outer wrapper
                style={{
                  backgroundColor: "white",
                  fontFamily: "'Montserrat'",
                  // marginBottom: "10px",
                }}
            
                // ✅ Style the inner input wrapper (e.g. padding, color)
                InputProps={{
                  ...params.InputProps,
                  style: {
                    fontFamily: "'Montserrat'",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#333",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
            
                // ✅ Style the actual text input
                inputProps={{
                  ...params.inputProps,
                  style: {
                    fontFamily: "'Montserrat'",
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#333",
                  }
                }}
                
                // Optional: label styling
                InputLabelProps={{
                  shrink: true,
                  style: { fontStyle: "italic" }
                }}
              />
            )}                   
          />
        </div>

    {/* Buttons */}

    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  {/* First Row - "Add to Reel" Button */}
                  <div style={{ marginBottom: "10px" }}> 
                      <Button onClick={validateConnection} variant="contained" style={{ fontFamily: "'Montserrat'" }}>
                          Add to Reel
                      </Button>
                  </div>

                  {/* Second Row - "Rewind" and "Start Over" */}
                  {route.length > 1 && (
                    <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                        <Button
                        onClick={undoLastMove}
                        variant="outlined"
                        style={{ fontFamily: "'Montserrat'" }}
                        >
                        ⏪ Rewind
                        </Button>

                        <Button
                        onClick={() => {
                            if (typeof gtag === "function") {
                              gtag('event', 'start_over', {
                                event_category: 'Game',
                                event_label: 'Start Over Clicked',
                                value: 1
                              });
                            }
                            saveGameData(startMovie, endMovie, steps_taken, false, "new_movie_clicked"); 
                            fetchRandomMovies(userId).then(() => {
                                handleStartGame();
                              });                              
                            setIsGameStarted(true); // ✅ Keep user in active game UI
                        }}
                        variant="outlined"
                        style={{ fontFamily: "'Montserrat'" }}
                        >
                        🔄 Start Over
                        </Button>
                    </div>
                    )}
              </div>
    </>

    </>
     )}

     {/* Feedback Form Link */}

     <div style={{
        textAlign: "center",
        fontFamily: "'Montserrat'",
        fontSize: "12px",
        padding: "20px",
        color: "#666",
        }}>
        {window.location.hostname.includes("breely") ? "BReely" : "Reely"} uses data from{" "}
        <a
            href="https://www.themoviedb.org/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#3f51b5", textDecoration: "underline" }}
        >
            TMDB
        </a>.
        <br />
        We're in beta —{" "}
        <a
            href="https://forms.gle/2S2pVvDajVmX7KWJ7"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#3f51b5", textDecoration: "underline" }}
        >
            share feedback
        </a>
        !
        <br />
        {/* Reddit */}
        <a
            href="https://www.reddit.com/r/playreely"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: "#3f51b5",
              textDecoration: "underline",
              marginTop: "6px"
            }}
          >
            <img
              src="/reddit_logo.png"
              alt="Reddit"
              style={{ width: "18px", height: "18px", marginRight: "6px" }}
            />
          </a>
          {/* Instagram */}
        <a
          href="https://instagram.com/playreely"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", color: "#3f51b5", textDecoration: "underline", marginTop: "6px" }}
        >
          <img
            src="/instagram_icon.png" // Replace with actual logo path
            alt="Instagram"
            style={{ width: "18px", height: "18px", marginRight: "6px" }}
          />
        </a>
        </div>


  </div>
    );

    return (
    <> 
       {/*
        {showOutageBanner && (
        <div style={{
          backgroundColor: "#fff3cd",
          color: "#856404",
          padding: "8px 16px",
          borderRadius: "8px",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "14px",
          marginBottom: "12px",
          textAlign: "center",
          border: "1px solid #ffeeba",
          maxWidth: "90%",
          margin: "12px auto"
        }}>
          🛠️ We’re experiencing temporary issues. Reely should work again soon!
        </div>
      )}*/}

        {renderMainGame()}
        {renderCongratsDialog()}
        {renderShareDialog()}
        {renderHowToPlayDialog()}
        <Analytics />
        <SpeedInsights />


      

        {showBreelyMaintenance && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "white",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontFamily: "'Montserrat'",
                textAlign: "center",
              }}>
                <h1>🎬 BReely — Coming Soon!</h1>
                <p>We're working on improving the Bollywood Reely experience. Stay tuned!</p>
              </div>
            )}


    </>
       
    );
}

export default App;